'use strict';
import ErrorStackParser from 'error-stack-parser';
import { version as PACKAGE_VERSION } from "../package.json"

import { config } from './config';
import { isLinkToMobileAppInDomains, getClassesRegExp, getLinkIfShouldBeProcessed } from './modules/OutlinkTracking';
import { appendNavigationTimingPagePerformanceMetrics, getPerformanceAPI } from './modules/performanceMetrics';
import { ClientHintsProvider, convertHintsToFlatUrlStructure } from "./modules/clientHints"
import { HeartBeat, PingLevel, validateHeartbeatTimersParam, DEFAULT_HEARTBEAT_TIMINGS } from "./modules/heartBeat"
import { CookiesService } from './modules/Cookies';
import { isVisible } from './modules/contentTracking';
import { isDefined, isFunction, isObject, isString, isNumberRepresentation } from "./utils/types"
import { query } from './utils/query';
import { indexOfArray } from './utils/indexOfArray';
import { getUrlParameter, purify, resolveRelativeReference, isSameHost, getPathName , startsUrlWith } from './utils/url';
import { stringStartsWith } from "./utils/string"
import { getSourceElement, getUnixTimestamp, removeEmptyElementsFromTheArrayRight } from './utils/helpers';
import { logConsoleError, logConsoleWarn } from './utils/logger';
import { sha1 } from './utils/sha1';
import { validateEcommerceOptionsParameter, validateTrackGoalOptionsParameter } from './utils/parameterValidation';

/** @import { CookiesConfig } from './modules/Cookies/types.js' */
/** @import { PingLevelType } from './modules/heartBeat/types.js' */
/** @import { EcommerceOptions } from './types.js' */
import { domainFixup, titleFixup, urlFixup } from './utils/fixup';
import { addEventListenerWrapper } from './utils/wrappers';
import { detectBrowserFeatures } from './utils/browserFeatures';
import { getPiwikUrlForOverlay } from './utils/pageOverlay';
import { getCrossDomainVisitorId, getVisitorIdFromUrl, replaceHrefForCrossDomainLink, crossDomainTrackingLinkDecorator as _crossDomainTrackingLinkDecorator, isLinkToDifferentDomainButSamePiwikWebsite } from './modules/crossDomainTracking';
import { beforeUnloadHandler } from './helpers/handlers';
import { organizeQueue } from './utils/initialQueueProcessing.js';


/**
 * @typedef {{
 *  sku: string | number,
 *  name?: string | number,
 *  category?: string | (string | boolean | undefined | null)[],
 *  price?: number | string,
 *  quantity?: number,
 *  brand?: string,
 *  variant?: string | number,
 *  customDimensions?: Record<string | number, string>,
 * }} Product
*/

/**
 * @typedef {{
 *  orderId: string | number,
 *  grandTotal: number | string,
 *  subTotal?: number | string,
 *  tax?: number | string,
 *  shipping?: number | string,
 *  discount?: number | string,
 * }} PaymentInformation
*/

/************************************************************
 * Private data
 ************************************************************/
/** @type {number | undefined} */
var expireDateTime,

    /** @type {Record<string, ((...args: unknown[]) => void)[]>} */
    eventHandlers = {},

    /* alias frequently used globals for added minification */
    documentAlias = document,
    navigatorAlias = navigator,
    windowAlias = window,

    /* performance timing */
    pagePerformanceMetricsAlreadySent = false,

    /* encode */
    encodeWrapper = windowAlias.encodeURIComponent,

    /* decode */
    decodeWrapper = windowAlias.decodeURIComponent,

    /* asynchronous tracker */
    /** @type {Tracker[]} */
    asyncTrackers = [],

    /* synchronous tracker */
    /** @type {Tracker[]} */
    syncTrackers = [],

    /* local Piwik */
    /** @type {any} */
    Piwik;

/************************************************************
 * Private methods
 ************************************************************/

/**
 * Safe getter of attributes from non-safe objects
 * 
 * @param {any} object
 * @param {string} attribute
 * @param {string} [defaultValue]
 * @returns {any}
 */
function _get(object, attribute, defaultValue) {
    if(!isDefined(defaultValue)) {
        defaultValue = '';
    }

    try {
        return object[attribute] || defaultValue;
    } catch (e) {
        return defaultValue;
    }
}



/**
 * @param {Record<string, unknown>} property
 */
function isObjectEmpty(property)
{
    if (!property) {
        return true;
    }

    var i;
    var isEmpty = true;
    for (i in property) {
        if (Object.prototype.hasOwnProperty.call(property, i)) {
            isEmpty = false;
        }
    }

    return isEmpty;
}

/*
 * apply wrapper
 *
 * @param array parameterArray An array comprising either:
 *      [ 'methodName', optional_parameters ]
 * or:
 *      [ functionObject, optional_parameters ]
 */

function apply() {
  for (let i = 0; i < arguments.length; i += 1) {
    const parameterArray = arguments[i];
    if (!Array.isArray(parameterArray)) {
      logConsoleError(
        `[${config.queueName}.push()]: expected array as argument, got ${JSON.stringify(parameterArray)}. Example: ${config.queueName}.push(['methodName', parameters]) or ${config.queueName}.push([function(){}])`
      );
      return;
    }
    
    const functionOrFunctionName = parameterArray.shift();

    for (let j = 0; j < asyncTrackers.length; j++) {
      const tracker = asyncTrackers[j];

      if (isFunction(functionOrFunctionName)) {
        functionOrFunctionName.apply(tracker, parameterArray);
        continue;
      }

      if (isString(functionOrFunctionName)) {
        if (/** @type {any} */ (tracker)[functionOrFunctionName]) {
          /** @type {any} */ (tracker)[functionOrFunctionName].apply(tracker, parameterArray);
        } else {
          var message = `The method '${functionOrFunctionName}' was not found in "${config.queueName}" variable.  Please have a look at the Piwik tracker documentation`;
          logConsoleError(message);
        }

        if (functionOrFunctionName === 'addTracker') {
          // addTracker adds an entry to asyncTrackers and would otherwise result in an endless loop
          break;
        }

        if (functionOrFunctionName === 'setTrackerUrl' || functionOrFunctionName === 'setSiteId') {
          // these two methods should be only executed on the first tracker
          break;
        }
      }
    }
  }
}

/**
 * 
 * @param {HTMLElement} element
 * @param {string} eventType
 * @param {EventListener} eventHandler
 * @param {boolean} useCapture
 */
function removeEventListener(element, eventType, eventHandler, useCapture) {
    if (element.removeEventListener) {
        element.removeEventListener(eventType, eventHandler, useCapture);

        return true;
    }

    // @ts-expect-error - browser compatibility
    if (element.detachEvent) {
        // @ts-expect-error - browser compatibility
        return element.detachEvent('on' + eventType, eventHandler);
    }

    // @ts-expect-error - browser compatibility
    if (element['on' + eventType] === eventHandler) {
        // @ts-expect-error - browser compatibility
        delete element['on' + eventType];
    }
}

/**
 * @private
 * Executes callback on page load (HTML)
 *
 * @param {() => void} callback
 */
function trackCallbackOnLoad(callback)
{
    if (documentAlias.readyState === 'complete') {
        callback();
    } else if (windowAlias.addEventListener) {
        windowAlias.addEventListener('load', callback);
    // TODO: PPI-1422 - it can be removed - attachEvent was used by older browsers
    // @ts-expect-error - browser compatibility
    } else if (windowAlias.attachEvent) {
        // @ts-expect-error - browser compatibility
        windowAlias.attachEvent('onload', callback);
    }
}

/**
 * @private
 * Executes callback when all resources are loaded
 *
 * @param {() => void} callback
 */
function trackCallbackOnReady(callback)
{
    var loaded = false;

    // TODO: PPI-1422 - it can be removed - attachEvent was used by older browsers
    // @ts-expect-error - browser compatibility
    if (documentAlias.attachEvent) {
        loaded = documentAlias.readyState === 'complete';
    } else {
        loaded = documentAlias.readyState !== 'loading';
    }

    if (loaded) {
        callback();
        return;
    }

    // @ts-expect-error - browser compatibility
    if (documentAlias.addEventListener) {
        addEventListenerWrapper(documentAlias, 'DOMContentLoaded', function ready() {
            documentAlias.removeEventListener('DOMContentLoaded', ready, false);
            if (!loaded) {
                loaded = true;
                callback();
            }
        });
    // @ts-expect-error - browser compatibility
    } else if (documentAlias.attachEvent) {
        // @ts-expect-error - browser compatibility
        documentAlias.attachEvent('onreadystatechange', function ready() {
            if (documentAlias.readyState === 'complete') {
                // @ts-expect-error - browser compatibility
                documentAlias.detachEvent('onreadystatechange', ready);
                if (!loaded) {
                    loaded = true;
                    callback();
                }
            }
        });

        // @ts-expect-error - browser compatibility
        if (documentAlias.documentElement.doScroll && windowAlias === windowAlias.top) {
            (function ready() {
                if (!loaded) {
                    try {
                        // @ts-expect-error browser compatibility
                        documentAlias.documentElement.doScroll('left');
                    } catch (error) {
                        setTimeout(ready, 0);

                        return;
                    }
                    loaded = true;
                    callback();
                }
            }());
        }
    }

    // fallback
    addEventListenerWrapper(windowAlias, 'load', function () {
        if (!loaded) {
            loaded = true;
            callback();
        }
    }, false);
}

/*
 * Get page referrer
 */
function getReferrer() {
    var referrer = '';

    try {
        referrer = windowAlias.top?.document.referrer || '';
    } catch (e) {
        if (windowAlias.parent) {
            try {
                referrer = windowAlias.parent.document.referrer;
            } catch (e2) {
                referrer = '';
            }
        }
    }

    if (referrer === '') {
        referrer = documentAlias.referrer;
    }

    return referrer;
}

/**
 * Extracts host name from URL
 * @param {string} url
 * @return {string}
 */
function getHost(url) {
    var link = documentAlias.createElement('a');
    link.href = url;
    return link.host;
}

/**
 * TODO: replace with String.prototype.trim()
 * 
 * @param {string} text 
 * @returns {string}
 */
function trim(text)
{
    if (text && String(text) === text) {
        return text.replace(/^\s+|\s+$/g, '');
    }

    return text;
}

/**
 * 
 * @param {Node} node
 * @param {Node} containedNode
 * @returns {boolean}
 */
function containsNodeElement(node, containedNode)
{
    if (!node || !containedNode) {
        return false;
    }

    if (node.contains) {
        return node.contains(containedNode);
    }

    if (node === containedNode) {
        return true;
    }

    if (node.compareDocumentPosition) {
        return !!(node.compareDocumentPosition(containedNode) & 16);
    }

    return false;
}

/************************************************************
 * Content Tracking
 ************************************************************/

var content = {
    CONTENT_ATTR: 'data-track-content',
    CONTENT_CLASS: 'piwikTrackContent',
    CONTENT_NAME_ATTR: 'data-content-name',
    CONTENT_PIECE_ATTR: 'data-content-piece',
    CONTENT_PIECE_CLASS: 'piwikContentPiece',
    CONTENT_TARGET_ATTR: 'data-content-target',
    CONTENT_TARGET_CLASS: 'piwikContentTarget',
    CONTENT_IGNOREINTERACTION_ATTR: 'data-content-ignoreinteraction',
    CONTENT_IGNOREINTERACTION_CLASS: 'piwikContentIgnoreInteraction',
    CONTENT_WATCHED_ATTR: 'data-content-watched',
    /** @type {Location | undefined} */
    location: undefined,

    findContentNodes: function ()
    {

        var cssSelector  = '.' + this.CONTENT_CLASS;
        var attrSelector = '[' + this.CONTENT_ATTR + ']';
        var contentNodes = query.findMultiple([cssSelector, attrSelector]);

        return contentNodes;
    },
    /**
     * 
     * @param {HTMLElement | null} [node] 
     * @returns {HTMLElement[]}
     */
    findContentNodesWithinNode: function (node)
    {
        if (!node) {
            return [];
        }

        // NOTE: we do not use query.findMultiple here as querySelectorAll would most likely not deliver the result we want

        var nodes1 = query.findNodesHavingCssClass(node, this.CONTENT_CLASS);
        var nodes2 = query.findNodesHavingAttribute(node, this.CONTENT_ATTR);

        if (nodes2 && nodes2.length) {
            var index;
            for (index = 0; index < nodes2.length; index++) {
                nodes1.push(nodes2[index]);
            }
        }

        if (query.hasNodeAttribute(node, this.CONTENT_ATTR)) {
            nodes1.push(node);
        } else if (query.hasNodeCssClass(node, this.CONTENT_CLASS)) {
            nodes1.push(node);
        }

        nodes1 = query.makeNodesUnique(nodes1);

        return nodes1;
    },
    /**
     * @param {HTMLElement | null} anyNode 
     * @returns {HTMLElement | undefined}
     */
    findParentContentNode: function (anyNode)
    {
        if (!anyNode) {
            return;
        }

        var node    = anyNode;
        var counter = 0;

        // @ts-expect-error FIXME
        while (node && node !== documentAlias && node.parentNode) {
            if (query.hasNodeAttribute(node, this.CONTENT_ATTR)) {
                return node;
            }
            if (query.hasNodeCssClass(node, this.CONTENT_CLASS)) {
                return node;
            }

            // @ts-expect-error FIXME
            node = node.parentNode;

            if (counter > 1000) {
                break; // prevent loop, should not happen anyway but better we do this
            }
            counter++;
        }
    },
    /**
     * @param {HTMLElement} node 
     * @returns {HTMLElement}
     */
    findPieceNode: function (node)
    {
        var contentPiece;

        contentPiece = query.findFirstNodeHavingAttribute(node, this.CONTENT_PIECE_ATTR);

        if (!contentPiece) {
            contentPiece = query.findFirstNodeHavingClass(node, this.CONTENT_PIECE_CLASS);
        }

        if (contentPiece) {
            return contentPiece;
        }

        return node;
    },
    /**
     * @param {HTMLElement} [node]
     * @returns {HTMLElement | undefined}
     */
    findTargetNodeNoDefault: function (node)
    {
        if (!node) {
            return;
        }

        var target = query.findFirstNodeHavingAttributeWithValue(node, this.CONTENT_TARGET_ATTR);
        if (target) {
            return target;
        }

        target = query.findFirstNodeHavingAttribute(node, this.CONTENT_TARGET_ATTR);
        if (target) {
            return target;
        }

        target = query.findFirstNodeHavingClass(node, this.CONTENT_TARGET_CLASS);
        if (target) {
            return target;
        }
    },
    /**
     * @param {HTMLElement} node 
     * @returns {HTMLElement}
     */
    findTargetNode: function (node)
    {
        var target = this.findTargetNodeNoDefault(node);
        if (target) {
            return target;
        }

        return node;
    },
    /**
     * @param {HTMLElement} contentNode 
     * @returns {HTMLElement[] | undefined}
     */
    findTargetNodesNoDefault: function(contentNode){
        if(!contentNode) return;

        var nodes = query.findNodesHavingAttribute(contentNode, this.CONTENT_TARGET_ATTR)
        if(nodes && nodes.length > 0){
            return nodes
        }

        nodes = query.findNodesHavingCssClass(contentNode, this.CONTENT_TARGET_CLASS)
        if(nodes && nodes.length > 0){
            return nodes
        }

        return
    },
    /**
     * @param {HTMLElement} contentNode 
     * @returns {HTMLElement[]}
     */
    findTargetNodes: function (contentNode)
    {
        var targetNodes = content.findTargetNodesNoDefault(contentNode)
        if(targetNodes && targetNodes.length > 0){
            return targetNodes
        }

        return [contentNode]
    },
    /**
     * @param {HTMLElement} node 
     * @returns {string | undefined}
     */
    findContentName: function (node)
    {
        if (!node) {
            return;
        }

        var nameNode = query.findFirstNodeHavingAttributeWithValue(node, this.CONTENT_NAME_ATTR);

        if (nameNode) {
            return query.getAttributeValueFromNode(nameNode, this.CONTENT_NAME_ATTR);
        }

        var contentPiece = this.findContentPiece(node);
        if (contentPiece) {
            return this.removeDomainIfIsInLink(contentPiece);
        }

        if (query.hasNodeAttributeWithValue(node, 'title')) {
            return query.getAttributeValueFromNode(node, 'title');
        }

        var clickUrlNode = this.findPieceNode(node);

        if (query.hasNodeAttributeWithValue(clickUrlNode, 'title')) {
            return query.getAttributeValueFromNode(clickUrlNode, 'title');
        }

        var targetNode = this.findTargetNode(node);

        if (query.hasNodeAttributeWithValue(targetNode, 'title')) {
            return query.getAttributeValueFromNode(targetNode, 'title');
        }
    },
    /**
     * @param {HTMLElement} node 
     * @returns {string | undefined}
     */
    findContentPiece: function (node)
    {
        if (!node) {
            return;
        }

        var nameNode = query.findFirstNodeHavingAttributeWithValue(node, this.CONTENT_PIECE_ATTR);

        if (nameNode) {
            return query.getAttributeValueFromNode(nameNode, this.CONTENT_PIECE_ATTR);
        }

        var contentNode = this.findPieceNode(node);

        var media = this.findMediaUrlInNode(contentNode);
        if (media) {
            return this.toAbsoluteUrl(media);
        }
    },
    /**
     * @param {HTMLElement} node 
     * @returns {string | undefined}
     */
    findContentTarget: function (node)
    {
        if (!node) {
            return;
        }

        var targetNode = this.findTargetNode(node);

        if (query.hasNodeAttributeWithValue(targetNode, this.CONTENT_TARGET_ATTR)) {
            return query.getAttributeValueFromNode(targetNode, this.CONTENT_TARGET_ATTR);
        }

        var href;
        if (query.hasNodeAttributeWithValue(targetNode, 'href')) {
            href = /** @type {string} */ (query.getAttributeValueFromNode(targetNode, 'href'));
            return this.toAbsoluteUrl(href);
        }

        var contentNode = this.findPieceNode(node);

        if (query.hasNodeAttributeWithValue(contentNode, 'href')) {
            href = /** @type {string} */ (query.getAttributeValueFromNode(contentNode, 'href'));
            return this.toAbsoluteUrl(href);
        }
    },
    /**
     * @param {string} url 
     * @returns {boolean}
     */
    isSameDomain: function (url)
    {
        if (!url || !url.indexOf) {
            return false;
        }

        if (0 === url.indexOf(this.getLocation().origin)) {
            return true;
        }

        var posHost = url.indexOf(this.getLocation().host);
        if (8 >= posHost && 0 <= posHost) {
            return true;
        }

        return false;
    },
    /**
     * @param {string} text 
     * @returns {string}
     */
    removeDomainIfIsInLink: function (text)
    {
        // we will only remove if domain === location.origin meaning is not an outlink
        var regexContainsProtocol = '^https?://[^/]+';
        var regexReplaceDomain = '^.*//[^/]+';

        if (text &&
            text.search &&
            -1 !== text.search(new RegExp(regexContainsProtocol))
            && this.isSameDomain(text)) {

            text = text.replace(new RegExp(regexReplaceDomain), '');
            if (!text) {
                text = '/';
            }
        }

        return text;
    },
    /**
     * @param {Node | undefined} node 
     * @returns {string | undefined}
     */
    findMediaUrlInNode: function (node)
    {
        if (!node) {
            return;
        }

        var mediaElements = ['img', 'embed', 'video', 'audio'];
        var elementName   = node.nodeName.toLowerCase();

        if (-1 !== indexOfArray(mediaElements, elementName) &&
            query.findFirstNodeHavingAttributeWithValue(node, 'src')) {

            var sourceNode = query.findFirstNodeHavingAttributeWithValue(node, 'src');

            return query.getAttributeValueFromNode(sourceNode, 'src');
        }

        if (elementName === 'object' &&
            query.hasNodeAttributeWithValue(node, 'data')) {

            return query.getAttributeValueFromNode(node, 'data');
        }

        if (elementName === 'object') {
            var params = query.findNodesByTagName(node, 'param');
            if (params && params.length) {
                var index;
                for (index = 0; index < params.length; index++) {
                    if ('movie' === query.getAttributeValueFromNode(params[index], 'name') &&
                        query.hasNodeAttributeWithValue(params[index], 'value')) {

                        return query.getAttributeValueFromNode(params[index], 'value');
                    }
                }
            }

            var embed = query.findNodesByTagName(node, 'embed');
            if (embed && embed.length) {
                return this.findMediaUrlInNode(embed[0]);
            }
        }
    },
    /**
     * @param {string} text 
     * @returns {string}
     */
    trim: function (text)
    {
        return trim(text);
    },
    /**
     * @param {HTMLElement} node 
     * @returns {boolean}
     */
    isOrWasNodeInViewport: function (node)
    {
        if (!node || !node.getBoundingClientRect || node.nodeType !== 1) {
            return true;
        }

        var rect = node.getBoundingClientRect();
        var html = documentAlias.documentElement || {};

        var wasVisible = rect.top < 0;
        if (wasVisible && node.offsetTop) {
            wasVisible = (node.offsetTop + rect.height) > 0;
        }

        var docWidth = html.clientWidth; // The clientWidth attribute returns the viewport width excluding the size of a rendered scroll bar

        if (windowAlias.innerWidth && docWidth > windowAlias.innerWidth) {
            docWidth = windowAlias.innerWidth; // The innerWidth attribute must return the viewport width including the size of a rendered scroll bar
        }

        var docHeight = html.clientHeight; // The clientWidth attribute returns the viewport width excluding the size of a rendered scroll bar

        if (windowAlias.innerHeight && docHeight > windowAlias.innerHeight) {
            docHeight = windowAlias.innerHeight; // The innerWidth attribute must return the viewport width including the size of a rendered scroll bar
        }

        return (
            (rect.bottom > 0 || wasVisible) &&
            rect.right  > 0 &&
            rect.left   < docWidth &&
            ((rect.top  < docHeight) || wasVisible) // rect.top < 0 we assume user has seen all the ones that are above the current viewport
        );
    },
    /**
     * @param {HTMLElement} node 
     * @returns {boolean}
     */
    isNodeVisible: function (node)
    {
        var isItVisible  = isVisible(node);
        var isInViewport = this.isOrWasNodeInViewport(node);
        return isItVisible && isInViewport;
    },
    /**
     * @param {string | null | undefined} interaction 
     * @param {string | null | undefined} name 
     * @param {string | null | undefined} piece 
     * @param {string | null | undefined} target 
     * @returns {string}
     */
    buildInteractionRequestParams: function (interaction, name, piece, target)
    {
        var params = '';

        if (interaction) {
            params += 'c_i='+ encodeWrapper(interaction);
        }
        if (name) {
            if (params) {
                params += '&';
            }
            params += 'c_n='+ encodeWrapper(name);
        }
        if (piece) {
            if (params) {
                params += '&';
            }
            params += 'c_p='+ encodeWrapper(piece);
        }
        if (target) {
            if (params) {
                params += '&';
            }
            params += 'c_t='+ encodeWrapper(target);
        }

        return params;
    },
    /**
     * @param {string} name 
     * @param {string} piece 
     * @param {string | null} [target] 
     * @returns {string}
     */
    buildImpressionRequestParams: function (name, piece, target)
    {
        var params = 'c_n=' + encodeWrapper(name) +
                     '&c_p=' + encodeWrapper(piece);

        if (target) {
            params += '&c_t=' + encodeWrapper(target);
        }

        return params;
    },
    /**
     * @param {HTMLElement | undefined} node 
     * @returns {{ name: string, piece: string, target: string } | undefined}
     */
    buildContentBlock: function (node)
    {
        if (!node) {
            return;
        }

        var name   = this.findContentName(node);
        var piece  = this.findContentPiece(node);
        var target = this.findContentTarget(node);

        name   = name ? this.trim(name) : 'Unknown';
        piece  = piece ? this.trim(piece) : 'Unknown';
        target = target ? this.trim(target) : '';

        return {
            name: name,
            piece: piece,
            target: target
        };
    },
    /**
     * @param {HTMLElement[] | undefined} contentNodes 
     * @returns {{ name: string, piece: string, target: string }[]}
     */
    collectContent: function (contentNodes)
    {
        if (!contentNodes || !contentNodes.length) {
            return [];
        }

        var contents = [];

        var index, contentBlock;
        for (index = 0; index < contentNodes.length; index++) {
            contentBlock = this.buildContentBlock(contentNodes[index]);
            if (isDefined(contentBlock)) {
                contents.push(contentBlock);
            }
        }

        return contents;
    },
    /**
     * @param {Location | undefined} [location] 
     */
    setLocation: function (location)
    {
        this.location = location;
    },
    getLocation: function ()
    {
        var locationAlias = this.location || windowAlias.location;

        if (!locationAlias.origin) {
            // @ts-expect-error browser compatibility
            locationAlias.origin = locationAlias.protocol + "//" + locationAlias.hostname + (locationAlias.port ? ':' + locationAlias.port: '');
        }

        return locationAlias;
    },
    /**
     * @param {string} url 
     * @returns {string}
     */
    toAbsoluteUrl: function (url)
    {
        if ((!url || String(url) !== url) && url !== '') {
            // we only handle strings
            return url;
        }

        if ('' === url) {
            return this.getLocation().href;
        }

        // Eg //example.com/test.jpg
        if (url.search(/^\/\//) !== -1) {
            return this.getLocation().protocol + url;
        }

        // Eg http://example.com/test.jpg
        if (url.search(/:\/\//) !== -1) {
            return url;
        }

        // Eg #test.jpg
        if (0 === url.indexOf('#')) {
            return this.getLocation().origin + this.getLocation().pathname + url;
        }

        // Eg ?x=5
        if (0 === url.indexOf('?')) {
            return this.getLocation().origin + this.getLocation().pathname + url;
        }

        // Eg mailto:x@y.z tel:012345, ... market:... sms:..., javasript:... ecmascript: ... and many more
        if (0 === url.search('^[a-zA-Z]{2,11}:')) {
            return url;
        }

        // Eg /test.jpg
        if (url.search(/^\//) !== -1) {
            return this.getLocation().origin + url;
        }

        // Eg test.jpg
        var regexMatchDir = '(.*/)';
        var base = this.getLocation().origin + (this.getLocation().pathname?.match(new RegExp(regexMatchDir))?.[0] || '');
        return base + url;
    },
    /**
     * @param {string} url 
     * @returns {boolean}
     */
    isUrlToCurrentDomain: function (url) {

        var absoluteUrl = this.toAbsoluteUrl(url);

        if (!absoluteUrl) {
            return false;
        }

        var origin = this.getLocation().origin;
        if (origin === absoluteUrl) {
            return true;
        }

        if (0 === String(absoluteUrl).indexOf(origin)) {
            if (':' === String(absoluteUrl).substr(origin.length, 1)) {
                return false; // url has port whereas origin has not => different URL
            }

            return true;
        }

        return false;
    },
    /**
     * @param {HTMLElement} [node]
     * @param {string} [url]
     */
    setHrefAttribute: function (node, url)
    {
        if (!node || !url) {
            return;
        }

        query.setAnyAttribute(node, 'href', url);
    },
    /**
     * @param {HTMLElement} targetNode 
     * @returns {boolean}
     */
    shouldIgnoreInteraction: function (targetNode)
    {
        var hasAttr  = query.hasNodeAttribute(targetNode, this.CONTENT_IGNOREINTERACTION_ATTR);
        var hasClass = query.hasNodeCssClass(targetNode, this.CONTENT_IGNOREINTERACTION_CLASS);
        return hasAttr || hasClass;
    }
};

/**
 * Piwik Tracker class
 *
 * trackerUrl and trackerSiteId are optional arguments to the constructor
 *
 * See: Tracker.setTrackerUrl() and Tracker.setSiteId()
 * 
 * @constructor
 * @param {string} trackerUrl 
 * @param {string} siteId 
 */
export function Tracker(trackerUrl, siteId) {

    /************************************************************
     * Private members
     ************************************************************/

    var
        // Current URL and Referrer URL
        locationArray = urlFixup(documentAlias.domain, windowAlias.location.href, getReferrer()),
        domainAlias = domainFixup(locationArray[0]),
        locationHrefAlias = locationArray[1],
        configReferrerUrl = locationArray[2],

        enableJSErrorTracking = false,

        defaultRequestMethod = 'beacon',

        // Request method (GET, POST or beacon)
        configRequestMethod = defaultRequestMethod,

        ipTracking = true,

        deviceDataAnonymize = false,

        clientHintsEnabled = false,

        defaultRequestContentType = 'application/x-www-form-urlencoded; charset=UTF-8',

        // Request Content-Type header value; applicable when POST request method is used for submitting tracking events
        configRequestContentType = defaultRequestContentType,

        // Tracker URL
        configTrackerUrl = trackerUrl || '',

        // API URL (only set if it differs from the Tracker URL)
        configApiUrl = '',

        // This string is appended to the Tracker URL Request (eg. to send data that is not handled by the existing setters/getters)
        configAppendToTrackingUrl = '',

        // Site ID
        configTrackerSiteId = siteId || '',

        // user should be tracked anonymously
        configUserIsAnonymous = false,
        sessionIdStrictPrivacyMode = false,
        /** @type {boolean | null} */
        lastPageViewWasAnonymous = null,
        /** @type {boolean | null} */
        lastPageViewHadCookie = null,

        // User ID
        configUserId = '',

        // Visitor UUID
        visitorUUID = '',

        // chance to send another request at onLoad after trackPageView to collect timing data
        configUpdateTimingDataOnPageLoadSampling = 10,

        // Document URL
        /** @type {string | undefined} */
        configCustomUrl,

        // Document title
        configTitle = '',

        // Extensions to be treated as download links
        configDownloadExtensions = ['7z','aac','apk','arc','arj','asf','asx','avi','azw3','bin','csv','deb','dmg','doc','docx','epub','exe','flv','gif','gz','gzip','hqx','ibooks','jar','jpg','jpeg','js','mobi','mp2','mp3','mp4','mpg','mpeg','mov','movie','msi','msp','odb','odf','odg','ods','odt','ogg','ogv','pdf','phps','png','ppt','pptx','qt','qtm','ra','ram','rar','rpm','sea','sit','tar','tbz','tbz2','bz','bz2','tgz','torrent','txt','wav','wma','wmv','wpd','xls','xlsx','xml','z','zip'],

        // Hosts or alias(es) to not treat as outlinks
        configHostsAlias = [domainAlias],

        // HTML anchor element classes to not track
        /** @type {string[]} */
        configIgnoreClasses = [],

        // HTML anchor element classes to treat as downloads
        /** @type {string[]} */
        configDownloadClasses = [],

        // HTML anchor element classes to treat at outlinks
        /** @type {string[]} */
        configLinkClasses = [],

        // Maximum delay to wait for web bug image to be fetched (in milliseconds)
        configTrackerPause = 500,

        // Maximal allowed inactivity between heart beat and last request
        maxHeartBeatInactivity = 30 * 60 * 1000,

        /** @type {Date | null} */
        lastPageViewActivityTime = null,

        heartBeatShouldBeEnabled = false,
        
        // Disallow hash tags in URL
        configDiscardHashTag = false,

        // Custom data
        // this is used for internal testing purposes, not sure if anyone else uses it
        /** @type {Record<string | number, string | number | undefined>} */
        configCustomData,

        // First-party cookie name prefix
        configCookieNamePrefix = '_pk_',

        // the URL parameter that will store the visitorId if cross domain linking is enabled
        // pk_vid = visitor ID
        // first part of this URL parameter will be 16 char visitor Id.
        // The second part is the 10 char current timestamp and the third and last part will be a 6 characters deviceId
        // timestamp is needed to prevent reusing the visitorId when the URL is shared. The visitorId will be
        // only reused if the timestamp is less than 45 seconds old.
        // deviceId parameter is needed to prevent reusing the visitorId when the URL is shared. The visitorId
        // will be only reused if the device is still the same when opening the link.
        // VDI = visitor device identifier
        configVisitorIdUrlParameter = 'pk_vid',

        // Cross domain linking, the visitor ID is transmitted only in the 180 seconds following the click.
        configVisitorIdUrlParameterTimeoutInSeconds = 180,

        // First-party cookie domain
        // User agent defaults to origin hostname
        /** @type {string | undefined} */
        configCookieDomain,

        // First-party cookie path
        // Default is user agent defined.
        /** @type {string | undefined} */
        configCookiePath,

        // Whether to use "Secure" cookies that only work over SSL
        configCookieIsSecure = false,

        // First-party cookies are disabled
        configCookiesDisabled = false,

        // When true, tracker will set up Site Inspector configuration
        configSiteInspectorSetup = true,

        // Do Not Track
        configDoNotTrack = false,

        // Count sites which are pre-rendered
        configCountPreRendered = false,

        // Life of the visitor cookie (in milliseconds)
        configVisitorCookieTimeout = 33955200000, // 13 months (365 days + 28days)

        // Life of the session cookie (in milliseconds)
        configSessionCookieTimeout = 1800000, // 30 minutes

        // Is performance tracking enabled
        configPerformanceTrackingEnabled = true,

        // Generation time set from the server
        configPerformanceGenerationTime = 0,

        // Whether Custom Variables scope "visit" should be stored in a cookie during the time of the visit
        configStoreCustomVariablesInCookie = false,

        // Custom Variables read from cookie, scope "visit"
        /** @type {Record<number, [string, string]>} */
        customVariables,

        // Custom Variables, scope "page"
        /** @type {Record<number, [string, string]>} */
        customVariablesPage = {},

        // Custom Variables, scope "event"
        /** @type {Record<number, [string, string]>} */

        customVariablesEvent = {},
        /** @type {(query: string) => string | undefined} */
        customRequestProcessor,

        // Custom Dimensions (can be any scope)
        /** @type {Record<string, string>} */
        customDimensions = {},

        // Encoded custom dimensions
        /** @type {Record<string, string>} */
        customDimensionsEncoded = {},

        // Custom Variables names and values are each truncated before being sent in the request or recorded in the cookie
        customVariableMaximumLength = 200,

        // Ecommerce items
        /** @type {Record<string, [sku: string, name: string | undefined, category: string | string[] | undefined, price: number | undefined, quantity: number | undefined]>} */
        ecommerceItems = {},

        // Browser features via client-side data collection
        /** @type {Record<string, string>} */
        browserFeatures = {},

        // Keeps track of previously tracked content impressions
        /** @type {{ name: string, piece: string, target: string }[]} */
        trackedContentImpressions = [],
        isTrackOnlyVisibleContentEnabled = false,
        /** @type {IntersectionObserver | undefined} */
        visibleContentIntersectionObserver,

        // Guard to prevent empty visits see #6415. If there is a new visitor and the first 2 (or 3 or 4)
        // tracking requests are at nearly same time (eg trackPageView and trackContentImpression) 2 or more
        // visits will be created
        /** @type {number | undefined} */
        timeNextTrackingRequestCanBeExecutedImmediately,

        // Guard against installing the link tracker more than once per Tracker instance
        /** @type {false | (() => void)} */
        linkTrackingInstalled = false,
        linkTrackingEnabled = false,
        crossDomainTrackingEnabled = false,
        /**
         * URL decorator for cross domain tracking. It adds visitor ID as a URL parameter.
         * @param {string} url link URL
         * @param {string} value value of cross domain visitor ID
         * @param {string} name name of cross domain visitor ID parameter
         * @return {string} Decorated URL
         */
        crossDomainTrackingLinkDecorator = _crossDomainTrackingLinkDecorator,
        /** @type {(url: string, name: string) => string} */
        crossDomainTrackingLinkVisitorIdGetter = getUrlParameter,

        // Timestamp of last tracker request sent to Piwik
        /** @type {number | null} */
        lastTrackerRequestTime = null,

        // Internal state of the pseudo click handler
        /** @type {string | null | undefined} */
        lastButton,
        /** @type {HTMLElement | null} */
        lastTarget,

        // Hash function
        hash = sha1,

        // Domain hash value
        /** @type {string} */
        domainHash,

        /** @type {string | undefined} */
        configIdPageView,

        // we measure how many pageviews have been tracked so plugins can use it to eg detect if a
        // pageview was already tracked or not
        numTrackedPageviews = 0,

        // Should only unique JS errors be sent to Piwik?
        onlyUniqueJavaScriptErrors = false,
        // holds the actual javascript errors if enableJSErrorTracking is on, if the very same error is
        // happening multiple times, then it will be tracked only once within the same page view
        /** @type {string[]} */
        javaScriptErrors = [],

        configCookiesToDelete = ['id', 'ses', 'cvar'],

        // Tracking source name and version, passed in `ts_n` and `ts_v` parameters.
        trackingSourceName = 'jstc',
        trackingSourceVersion = PACKAGE_VERSION,
        // initializer framework 
        trackingSourceProvider = "",
        trackingSourceProviderVersion = "";

        const clientHintsProvider = ClientHintsProvider({
          onResolve: processQueue,
        });

    // Document title
    try {
        configTitle = documentAlias.title;
    } catch(e) {
        configTitle = '';
    }

    /**
     * @returns {CookiesConfig}
     */
    const getCookiesConfig = () => ({
      cookiesDisabled: configCookiesDisabled,
      domain: configCookieDomain,
      path: configCookiePath,
      isSecure: configCookieIsSecure,
      trackerSiteId: configTrackerSiteId,
      cookieNamePrefix: configCookieNamePrefix,
      domainHash: domainHash,
    })

    const heartBeat = HeartBeat({
      onPing: heartBeatPingIfActivity
    });

    const cookiesService = CookiesService({
      decodeWrapper,
      encodeWrapper,
      getCookiesConfig,
      documentAlias,
      navigatorAlias
    });
    
    /**
     * @param {PingLevelType} pingLevel
     */
    function heartBeatPingIfActivity(pingLevel){
      if (!shouldSendHeartBeat()) {
        return;
      }

      const now = new Date();
      if (
        lastPageViewActivityTime !== null &&
        now.getTime() - lastPageViewActivityTime.getTime() > maxHeartBeatInactivity
      ) {
        return;
      }

      const pingRequest = getRequest(`ping=${pingLevel}`, null, 'ping');
      const isPriorityPing = pingLevel !== PingLevel.PeriodicHeartbeat;
      sendRequest(
        pingRequest,
        configTrackerPause,
        null,
        isPriorityPing,
        isPriorityPing ? 'beacon' : undefined
      );
    }

    // TODO: PPI-1422 - it can be removed - it's there because of support of older browsers.  
    /**
     * Send image request to Piwik server using GET.
     * The infamous web bug (or beacon) is a transparent, single pixel (1x1) image
     * 
     * @param {string} request
     * @param {(() => void) | null} [callback]
     */
    function getImage(request, callback) {
        var image = new Image(1, 1);

        image.onload = function () {
            if (isFunction(callback)) { callback(); }
        };
        // make sure to actually load an image so callback gets invoked
        if(request.indexOf('send_image=0') !== -1) {
            request = request.replace("&send_image=0","&send_image=1");
        }else {
            request += '&send_image=1';
        }
        image.src = configTrackerUrl + (configTrackerUrl.indexOf('?') < 0 ? '?' : '&') + request;
    }

    // TODO: PPI-1422 - it can be refactored/improved - axios/fetch can be used
    /**
     * POST request to Piwik server using XMLHttpRequest.
     * 
     * @param {string} request
     * @param {(() => void) | null} [callback]
     * @param {boolean} [fallbackToGet]
     */
    function sendXmlHttpRequest(request, callback, fallbackToGet) {
        if (!isDefined(fallbackToGet) || null === fallbackToGet) {
            fallbackToGet = true;
        }

        try {
            // TODO: PPI-1422 - it can be removed - IE part
            // we use the progid Microsoft.XMLHTTP because
            // IE5.5 included MSXML 2.5; the progid MSXML2.XMLHTTP
            // is pinned to MSXML2.XMLHTTP.3.0
            var xhr = windowAlias.XMLHttpRequest
                ? new windowAlias.XMLHttpRequest()
                // @ts-expect-error browser compatibility
                : windowAlias.ActiveXObject
                // @ts-expect-error browser compatibility
                // eslint-disable-next-line no-undef
                ? new ActiveXObject('Microsoft.XMLHTTP')
                : null;

            xhr.open('POST', configTrackerUrl, true);

            // fallback on error
            xhr.onreadystatechange = function () {
                if (this.readyState === 4 && !(this.status >= 200 && this.status < 300) && fallbackToGet) {
                    getImage(request, callback);
                } else {
                    if (this.readyState === 4 && (typeof callback === 'function')) { callback(); }
                }
            };

            xhr.setRequestHeader('Content-Type', configRequestContentType);

            xhr.send(request);
        } catch (e) {
            if (fallbackToGet) {
                // fallback
                getImage(request, callback);
            }
        }
    }

    /**
     * @param {number} delay 
     */
    function setExpireDateTime(delay) {

        var now  = new Date();
        var time = now.getTime() + delay;

        if (!expireDateTime || time > expireDateTime) {
            expireDateTime = time;
        }
    }

    function shouldSendHeartBeat() {
        return lastPageViewWasAnonymous === false || lastPageViewHadCookie === true;
    }

    /**
     * @param {() => void} callback
     * @param {boolean} waitOnUnload
     */
    function makeSureThereIsAGapAfterFirstTrackingRequestToPreventMultipleVisitorCreation(callback, waitOnUnload)
    {
        var now     = new Date();
        var timeNow = now.getTime();

        lastTrackerRequestTime = timeNow;

        if (timeNextTrackingRequestCanBeExecutedImmediately && timeNow < timeNextTrackingRequestCanBeExecutedImmediately) {
            // we are in the time frame shortly after the first request. we have to delay this request a bit to make sure
            // a visitor has been created meanwhile.

            var timeToWait = timeNextTrackingRequestCanBeExecutedImmediately - timeNow;

            setTimeout(callback, timeToWait);
             if (waitOnUnload) {
                setExpireDateTime(timeToWait + 50); // set timeout is not necessarily executed at timeToWait so delay a bit more
            }
            timeNextTrackingRequestCanBeExecutedImmediately += 50; // delay next tracking request by further 50ms to next execute them at same time

            return;
        }

        if (timeNextTrackingRequestCanBeExecutedImmediately === undefined) {
            // it is the first request, we want to execute this one directly and delay all the next one(s) within a delay.
            // All requests after this delay can be executed as usual again
            var delayInMs = 800;
            timeNextTrackingRequestCanBeExecutedImmediately = timeNow + delayInMs;
        }

        callback();
    }

    /**
     * @type {Array<
     * {type: "single", request: string, delay: number, callback?: (() => void) | null, force?: boolean, method?: string} |
     * {type: "bulk", requests: string[], delay: number}
     * >}
     */
    const requestQueue = []

    function processQueue() {
      for (let i = 0; i < requestQueue.length; i++) {
        const request = requestQueue[i];
        if (request.type === 'single') {
          sendRequest(
            request.request,
            request.delay,
            request.callback,
            request.force,
            request.method,
          );
          continue;
        }

        if (request.type === 'bulk') {
          sendBulkRequest(request.requests, request.delay);
          continue;
        }

        throw new Error(`[processQueue] unprocessable request type: ${request}`);
      }
    }

    //  TODO: PPI-1422 - it can be refactored/improved - axios/fetch can be used
    /**
     * @param {string} request
     * @param {number} delay
     * @param {(() => void) | null} [callback]
     * @param {boolean} [force]
     * @param {string} [method]
     */
    function sendRequest(request, delay, callback, force, method) {
        if (clientHintsEnabled) {
          const chState = clientHintsProvider.getState();
          if (chState.state === 'loading') {
            requestQueue.push({
              type: 'single',
              request,
              delay,
              callback,
              force,
              method,
            });
            return;
          }

          if (chState.state === 'available') {
            request += convertHintsToFlatUrlStructure(chState.hints)
          }
        }

        if (isFunction(customRequestProcessor)) {
            // falsy value returned from request processor cancels the request
            request = customRequestProcessor(request) || '';
        }

        if (!configDoNotTrack && request) {
            var preferredMethod = method || configRequestMethod;
            var canSendBeaconRequest = (
                !callback &&
                // @ts-expect-error browser compatibility
                navigatorAlias.sendBeacon &&
                windowAlias.Blob &&
                request.length <= 65536
            );
            var waitOnUnload = true;
            var sendRequestCall = function () {
                if (preferredMethod === 'beacon' && canSendBeaconRequest) {
                    var blob = new windowAlias.Blob([String(request)], {type: configRequestContentType});
                    // Request added to POST query for debug purposes
                    if (navigatorAlias.sendBeacon(configTrackerUrl, blob)) {
                        waitOnUnload = false;
                    } else {
                        sendXmlHttpRequest(request, callback);
                    }
                } else if (preferredMethod === 'POST' || String(request).length > 2000) {
                    sendXmlHttpRequest(request, callback);
                } else {
                    getImage(request, callback);
                }

                if (waitOnUnload) {
                    setExpireDateTime(delay);
                }
            };
            if (force) {
                sendRequestCall();
            } else {
                makeSureThereIsAGapAfterFirstTrackingRequestToPreventMultipleVisitorCreation(sendRequestCall, waitOnUnload);
            }
        }
    }

    /**
     * @param {string[] | undefined} requests
     * @returns {boolean}
     */
    function canSendBulkRequest(requests)
    {
        if (configDoNotTrack) {
            return false;
        }

        return requests ? requests.length > 0 : false;
    }

    //  TODO: PPI-1422 - it can be refactored/improved - axios/fetch can be used    
    /**
     * Send requests using bulk
     * @param {string[]} requests
     * @param {number} delay
     */
    function sendBulkRequest(requests, delay)
    {
        if (!canSendBulkRequest(requests)) {
            return;
        }

        if (clientHintsEnabled) {
          const chState = clientHintsProvider.getState();
          if (chState.state === 'loading') {
            requestQueue.push({ type: 'bulk', requests, delay });
            return;
          }

          if (chState.state === 'available') {
            for (let i = 0; i < requests.length; i++) {
              requests[i] += convertHintsToFlatUrlStructure(chState.hints)
            }
          }
        }

        
        if (isFunction(customRequestProcessor)) {
          const processedRequests = [];
          for (let i = 0; i < requests.length; i++) {
            const processedRequest = customRequestProcessor(requests[i]);
            if (processedRequest) {
              processedRequests.push(processedRequest);
            }
          }
          // nothing to send
          if (processedRequests.length <= 0) {
            return;
          }

          requests = processedRequests;
        }

        var bulk = '{"requests":["?' + requests.join('","?') + '"]}';

        makeSureThereIsAGapAfterFirstTrackingRequestToPreventMultipleVisitorCreation(function () {
            sendXmlHttpRequest(bulk, null, false);
            if (configRequestMethod !== 'beacon') {
                setExpireDateTime(delay);
            }
        }, false);
    }

    /*
     * Update domain hash
     */
    function updateDomainHash() {
        domainHash = hash((configCookieDomain || domainAlias) + (configCookiePath || '/')).slice(0, 4); // 4 hexits = 16 bits
    }

    /**
     * NOTE: this method is not publicly documented nor it is used internally BUT it is mentioned in a [changelog](https://developers.piwik.pro/changelog/16-33-0/)
     *
     * @param {number} maxLength
     */
    this.setCustomVariableMaximumLength = function(maxLength) {
        customVariableMaximumLength = maxLength;
    }

    /*
     * Inits the custom variables object
     */
    function getCustomVariablesFromCookie() {
        var cookieName = cookiesService.getCookieName('cvar'),
            cookie = cookiesService.getCookie(cookieName);

        if (isString(cookie) && cookie.length) {
            cookie = JSON.parse(cookie);

            if (isObject(cookie)) {
                return cookie;
            }
        }

        return {};
    }

    /*
     * Lazy loads the custom variables from the cookie, only once during this page view
     */
    function loadCustomVariables() {
        if (!customVariables) {
            customVariables = getCustomVariablesFromCookie();
        }
    }

    /*
     * Generate a pseudo-unique ID to fingerprint this user
     * 16 hexits = 64 bits
     * note: this isn't a RFC4122-compliant UUID
     */
    function generateRandomUuid() {
        return hash(
            (navigatorAlias.userAgent || '') +
            (navigatorAlias.platform || '') +
            JSON.stringify(browserFeatures) +
            (new Date()).getTime() +
            Math.random()
        ).slice(0, 16);
    }

    /**
     * Load visitor ID cookie
     * @typedef {[
     *  newVisit: "0" | "1",
     *  uuid: string,
     *  creationTimestamp: number | string,
     *  visitCount: number | string,
     *  currentVisitTs: number | string,
     *  lastVisitTs: number | string | undefined,
     *  lastEcommerceOrderTs: number | string | undefined
     * ]} VisitorIdCookieValuesArray
     */

    /**
     * @returns {VisitorIdCookieValuesArray}
     */
    function loadVisitorIdCookie() {
        if (!visitorUUID) {
            // we are using locationHrefAlias and not currentUrl on purpose to for sure get the passed URL parameters
            // from original URL
            visitorUUID = getVisitorIdFromUrl(
              locationHrefAlias,
              browserFeatures,
              {
                crossDomainTrackingEnabled,
                configVisitorIdUrlParameter,
                configVisitorIdUrlParameterTimeoutInSeconds,
              },
              crossDomainTrackingLinkVisitorIdGetter
            );
        }

        var now = new Date(),
            nowTs = getUnixTimestamp(now),
            visitorIdCookieName = cookiesService.getCookieName('id'),
            id = cookiesService.getCookie(visitorIdCookieName),
            /** @type {VisitorIdCookieValuesArray} */
            cookieValue,
            uuid;

        // Visitor ID cookie found
        if (isString(id) && id.length) {
            cookieValue = /** @type {VisitorIdCookieValuesArray} */ (id.split('.'));
            // @ts-expect-error some unknown legacy
            cookieValue[0] = cookiesService.transformIfAMPCookie(cookieValue[0]);

            if (cookieValue.length < 6) {
                // creation timestamp - seconds since Unix epoch
                // @ts-expect-error shifted by 1
                cookieValue[1] = nowTs;
                // visitCount - 0 = no previous visit
                cookieValue[2] = 0;
                // current visit timestamp
                cookieValue[3] = nowTs;
                // last visit timestamp - blank = no previous visit
                cookieValue[4] = '';
                // last ecommerce order timestamp
                cookieValue[5] = '';
            }

            // returning visitor flag
            cookieValue.unshift('0');

            if(visitorUUID.length) {
                cookieValue[1] = visitorUUID;
            }
            return cookieValue;
        }

        if(visitorUUID.length) {
            uuid = visitorUUID;
        } else if ('0' === cookiesService.hasCookies()){
            uuid = '';
        } else {
            uuid = generateRandomUuid();
        }

        // No visitor ID cookie, let's create a new one
        cookieValue = [
            // new visitor
            '1',

            // uuid
            uuid,

            // creation timestamp - seconds since Unix epoch
            nowTs,

            // visitCount - 0 = no previous visit
            0,

            // current visit timestamp
            nowTs,

            // last visit timestamp - blank = no previous visit
            '',

            // last ecommerce order timestamp
            ''
        ];

        return cookieValue;
    }


    /**
     * Loads the Visitor ID cookie and returns a named array of values
     */
    function getValuesFromVisitorIdCookie() {
        var cookieVisitorIdValue = loadVisitorIdCookie(),
            newVisitor = cookieVisitorIdValue[0],
            uuid = cookieVisitorIdValue[1],
            createTs = cookieVisitorIdValue[2],
            visitCount = cookieVisitorIdValue[3],
            currentVisitTs = cookieVisitorIdValue[4],
            lastVisitTs = cookieVisitorIdValue[5];

        // case migrating from pre-1.5 cookies
        if (!isDefined(cookieVisitorIdValue[6])) {
            cookieVisitorIdValue[6] = "";
        }

        var lastEcommerceOrderTs = cookieVisitorIdValue[6];

        return {
            newVisitor: newVisitor,
            uuid: uuid,
            createTs: createTs,
            visitCount: visitCount,
            currentVisitTs: currentVisitTs,
            lastVisitTs: lastVisitTs,
            lastEcommerceOrderTs: lastEcommerceOrderTs
        };
    }

    /**
     * Sets the Visitor ID cookie
     * 
     * @param {{
     * uuid: string | number,
     * createTs: string | number,
     * visitCount: string | number,
     * currentVisitTs: string | number,
     * lastVisitTs: string | number | undefined,
     * lastEcommerceOrderTs: string | number | undefined
     * }} [visitorIdCookieValues]
     */
    function setVisitorIdCookie(visitorIdCookieValues) {

        if(!configTrackerSiteId) {
            // when called before Site ID was set
            return;
        }

        if(!isDefined(visitorIdCookieValues)) {
            visitorIdCookieValues = getValuesFromVisitorIdCookie();
        }

        var cookieValue = visitorIdCookieValues.uuid + '.' +
            visitorIdCookieValues.createTs + '.' +
            visitorIdCookieValues.visitCount + '.' +
            visitorIdCookieValues.currentVisitTs + '.' +
            visitorIdCookieValues.lastVisitTs + '.' +
            visitorIdCookieValues.lastEcommerceOrderTs;

            cookiesService.setCookie(cookiesService.getCookieName('id'), cookieValue, { msToExpire: configVisitorCookieTimeout });
    }


    function deleteCookies() {
        var savedConfigCookiesDisabled = configCookiesDisabled;

        // Temporarily allow cookies just to delete the existing ones
        configCookiesDisabled = false;

        var index, cookieName;

        for (index = 0; index < configCookiesToDelete.length; index++) {
            cookieName = cookiesService.getCookieName(configCookiesToDelete[index]);
            if (0 !== cookiesService.getCookie(cookieName)) {
                cookiesService.deleteCookie(cookieName);
            }
        }

        configCookiesDisabled = savedConfigCookiesDisabled;
    }

    /**
     * @param {string} piwikUrl 
     * @param {string} siteId 
     */
    function siteInspectorSetUp(piwikUrl, siteId) {
        var script = documentAlias.createElement('script');

        script.setAttribute('id', 'ppas_container_configuration')
        script.setAttribute('data-appid', siteId);
        script.setAttribute('data-host', getHost(piwikUrl));

        documentAlias.getElementsByTagName('head')[0].appendChild(script);
    }

    /**
     * @this {Tracker}
     * @param {string} siteId 
     */
    function setSiteId(siteId) {
        configTrackerSiteId = siteId;
        setVisitorIdCookie();
        if (configSiteInspectorSetup) {
            siteInspectorSetUp(this.getTrackerUrl(), siteId);
        }
    }

    /**
     * NOTE: it is only used for custom variables
     * 
     * @param {Record<string, [string, string]>} value
     * @returns {Record<string, [string, string]>}
     */
    function sortCustomVariables(value) {
        // Object.keys(value) is not supported by all browsers, we get the keys manually
        var keys = [];
        var key;

        for (key in value) {
            if (Object.prototype.hasOwnProperty.call(value, key)) {
                keys.push(key);
            }
        }

        /** @type {Record<string, [string, string]>} */
        var normalized = {};
        keys.sort();
        var len = keys.length;
        var i;

        for (i = 0; i < len; i++) {
            normalized[keys[i]] = value[keys[i]];
        }

        return normalized;
    }

    /**
     * Creates the session cookie
     */
    function setSessionCookie() {
        cookiesService.setCookie(cookiesService.getCookieName('ses'), '*', { msToExpire: configSessionCookieTimeout });
    }

    function generateUniqueId() {
        var id = '';
        var chars = 'abcdefghijklmnopqrstuvwxyz0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        var charLen = chars.length;
        var i;

        for (i = 0; i < 6; i++) {
            id += chars.charAt(Math.floor(Math.random() * charLen));
        }

        return id;
    }

    //  TODO: PPI-1422 - it can be refactored/improved - axios/fetch can be used
    /**
     * @private
     * Returns the URL to call ppms.php,
     * with the standard parameters (plugins, resolution, url, referrer, etc.).
     * Sends the pageview and browser settings with every request in case of race conditions.
     * 
     * @param {string} request
     * @param {Record<string | number, string | number | undefined> | null} [customData]
     * @param {string} [pluginMethod]
     * @param {number | string} [currentEcommerceOrderTs]
     */
    function getRequest(request, customData, pluginMethod, currentEcommerceOrderTs) {
        var i,
            isNotSetYet,
            now = new Date(),
            nowTs = getUnixTimestamp(now),
            customVariablesCopy = customVariables,
            cookieSessionName = cookiesService.getCookieName('ses'),
            cookieCustomVariablesName = cookiesService.getCookieName('cvar'),
            cookieSessionValue = cookiesService.getCookie(cookieSessionName),
            currentUrl = configCustomUrl || locationHrefAlias;

        if (configCookiesDisabled) {
            deleteCookies();
        }

        if (configDoNotTrack) {
            return '';
        }

        var cookieVisitorIdValues = getValuesFromVisitorIdCookie();
        if (!isDefined(currentEcommerceOrderTs)) {
            currentEcommerceOrderTs = "";
        }

        // send charset if document charset is not utf-8. sometimes encoding
        // of urls will be the same as this and not utf-8, which will cause problems
        // do not send charset if it is utf8 since it's assumed by default in Piwik
        /** @type {string | undefined} */
        var charSet = documentAlias.characterSet || documentAlias.charset;

        if (!charSet || charSet.toLowerCase() === 'utf-8') {
            charSet = undefined;
        }

        if (!cookieSessionValue) {
            // cookie 'ses' was not found: we consider this the start of a 'session'

            // here we make sure that if 'ses' cookie is deleted few times within the visit
            // and so this code path is triggered many times for one visit,
            // we only increase visitCount once per Visit window (default 30min)
            var visitDuration = configSessionCookieTimeout / 1000;
            if (!cookieVisitorIdValues.lastVisitTs
                || (nowTs - Number(cookieVisitorIdValues.lastVisitTs)) > visitDuration) {
                cookieVisitorIdValues.visitCount = Number(cookieVisitorIdValues.visitCount) + 1;
                cookieVisitorIdValues.lastVisitTs = cookieVisitorIdValues.currentVisitTs;
                cookieVisitorIdValues.currentVisitTs = nowTs;
            }
        }

        if (stringStartsWith(request, 'action_name=')) {
            lastPageViewWasAnonymous = configUserIsAnonymous;
            lastPageViewHadCookie = Boolean(cookieVisitorIdValues.uuid);
            lastPageViewActivityTime = new Date();
        }

        // build out the rest of the request
        request += '&idsite=' + configTrackerSiteId +
        '&rec=1' +
        '&r=' + String(Math.random()).slice(2, 8) + // keep the string to a minimum
        '&h=' + now.getHours() + '&m=' + now.getMinutes() + '&s=' + now.getSeconds() +
        '&url=' + encodeWrapper(purify(currentUrl, { discardHashTag: configDiscardHashTag, visitorIdUrlParameter: configVisitorIdUrlParameter })) +
        (configReferrerUrl.length ? '&urlref=' + encodeWrapper(purify(configReferrerUrl, { discardHashTag: configDiscardHashTag, visitorIdUrlParameter: configVisitorIdUrlParameter })) : '') +
        ((configUserId && configUserId.length) ? '&uid=' + encodeWrapper(configUserId) : '') +
        (configUserIsAnonymous ? '&uia=1' : '') +
        (cookieVisitorIdValues.uuid ? '&_id=' + cookieVisitorIdValues.uuid : '') +
        '&_idts=' + cookieVisitorIdValues.createTs + '&_idvc=' + cookieVisitorIdValues.visitCount +
        (cookieVisitorIdValues.lastVisitTs ? '&_viewts=' + cookieVisitorIdValues.lastVisitTs : '') +
        (String(cookieVisitorIdValues.lastEcommerceOrderTs).length ? '&_ects=' + cookieVisitorIdValues.lastEcommerceOrderTs : '') +
        (charSet ? '&cs=' + encodeWrapper(charSet) : '') +
        '&ts_n=' + encodeWrapper(trackingSourceProvider || trackingSourceName) +
        '&ts_v=' + encodeWrapper(trackingSourceProviderVersion || trackingSourceVersion) +
        (ipTracking ? '' : '&rmip=1') +
        (deviceDataAnonymize && configUserIsAnonymous ? '&dda=1' : '');

        // browser features
        if (!sessionIdStrictPrivacyMode) {
            for (i in browserFeatures) {
                if (Object.prototype.hasOwnProperty.call(browserFeatures, i)) {
                    request += '&' + i + '=' + browserFeatures[i];
                }
            }
        }

        var customDimensionIdsAlreadyHandled = [];
        if (customData) {
            for (i in customData) {
                if (Object.prototype.hasOwnProperty.call(customData, i) && /^dimension\d+$/.test(i)) {
                    var index = i.replace('dimension', '');
                    customDimensionIdsAlreadyHandled.push(parseInt(index, 10));
                    customDimensionIdsAlreadyHandled.push(String(index));
                    request += '&' + i + '=' + customData[i];
                    delete customData[i];
                }
            }
        }

        if (customData && isObjectEmpty(customData)) {
            customData = null;
            // we deleted all keys from custom data
        }

        // custom dimensions
        for (i in customDimensions) {
            if (Object.prototype.hasOwnProperty.call(customDimensions, i)) {
                isNotSetYet = (-1 === indexOfArray(customDimensionIdsAlreadyHandled, i));
                if (isNotSetYet) {
                    // TODO: PPI-1422 - it can be removed - condition for IE
                    //We want to escape only if one of characters is out of `Basic Latin` (\u0000-\u007F) character range.
                    // eslint-disable-next-line no-control-regex
                    var unicodeRegex = new RegExp('[^\u0000-\u007F]');
                    if ((navigator.userAgent.indexOf('MSIE') !== -1 || navigator.appVersion.indexOf('Trident/') > 0) && (customDimensions[i].match(unicodeRegex) !== null)) {
                        request += '&dimension' + i + '=' + encodeURIComponent(customDimensions[i]);
                    } else {
                        request += '&dimension' + i + '=' + customDimensions[i];
                    }
                }
            }
        }

        for (i in customDimensionsEncoded) {
            if (Object.prototype.hasOwnProperty.call(customDimensionsEncoded, i)) {
                isNotSetYet = (-1 === indexOfArray(customDimensionIdsAlreadyHandled, i));
                if (isNotSetYet) {
                    request += '&dimension' + encodeURIComponent(i) + '=' + encodeURIComponent(customDimensionsEncoded[i]);
                }
            }
        }

        // custom data
        if (customData) {
            request += '&data=' + encodeWrapper(JSON.stringify(customData));
        } else if (configCustomData) {
            request += '&data=' + encodeWrapper(JSON.stringify(configCustomData));
        }

        // Custom Variables, scope "page"
        /**
         * @param {Record<string, [string, string]>} customVariables
         * @param {string} parameterName
         * @returns 
         */
        function appendCustomVariablesToRequest(customVariables, parameterName) {
            return '&' + parameterName + '=' + encodeWrapper(JSON.stringify(customVariables));
        }

        if(!isObjectEmpty(customVariablesPage)) {
            const sortedCustomVarPage = sortCustomVariables(customVariablesPage);
            request += appendCustomVariablesToRequest(sortedCustomVarPage, 'cvar');
        }

        if(!isObjectEmpty(customVariablesEvent)) {
            const sortedCustomVarEvent = sortCustomVariables(customVariablesEvent);
            request += appendCustomVariablesToRequest(sortedCustomVarEvent, 'e_cvar');
        }


        // Custom Variables, scope "visit"
        if (customVariables) {
            request += appendCustomVariablesToRequest(customVariables, '_cvar');

            // Don't save deleted custom variables in the cookie
            for (i in customVariablesCopy) {
                if (Object.prototype.hasOwnProperty.call(customVariablesCopy, i)) {
                    if (customVariables[i][0] === '' || customVariables[i][1] === '') {
                        delete customVariables[i];
                    }
                }
            }

            if (configStoreCustomVariablesInCookie) {
                cookiesService.setCookie(cookieCustomVariablesName, JSON.stringify(customVariables), { 
                  msToExpire: configSessionCookieTimeout 
                });
            }
        }

        // performance tracking
        var performanceAlias = getPerformanceAPI();
        var performanceGenerationTime = null;
        if (configPerformanceTrackingEnabled) {
            if (configPerformanceGenerationTime) {
                performanceGenerationTime = configPerformanceGenerationTime;
            } else if (performanceAlias && performanceAlias.requestStart && performanceAlias.responseEnd) {
                performanceGenerationTime = performanceAlias.responseEnd - performanceAlias.requestStart;
            }

            if (performanceGenerationTime !== null) {
                request += '&gt_ms=' + Math.max(performanceGenerationTime, 0);
            }
        }


        // page performance metrics
        const isPageViewOrPingRequest = pluginMethod === 'log' || pluginMethod === 'ping';

        const allNecessaryMetricsAreAvailable = performanceAlias && performanceAlias.loadEventEnd && performanceAlias.loadEventEnd !== 0 && performanceAlias.fetchStart && performanceAlias.fetchStart !== 0;

        if (configPerformanceTrackingEnabled && isPageViewOrPingRequest && numTrackedPageviews <= 2 && !pagePerformanceMetricsAlreadySent && allNecessaryMetricsAreAvailable) {
            // @ts-expect-error performanceAlias presence is checked in `allNecessaryMetricsAreAvailable`
            request += appendNavigationTimingPagePerformanceMetrics(performanceAlias);
            pagePerformanceMetricsAlreadySent = true;
        }

        if (configIdPageView) {
            request += '&pv_id=' + configIdPageView;
        }

        // update cookies
        cookieVisitorIdValues.lastEcommerceOrderTs = isDefined(currentEcommerceOrderTs) && String(currentEcommerceOrderTs).length ? currentEcommerceOrderTs : cookieVisitorIdValues.lastEcommerceOrderTs;
        setVisitorIdCookie(cookieVisitorIdValues);
        setSessionCookie();

        if (configAppendToTrackingUrl.length) {
            request += '&' + configAppendToTrackingUrl;
        }

        return request;
    }

    /**
     * @param {Product} product 
     */
    function ecommerceValidateProduct(product) {
        if(!isObject(product)) {
            logConsoleError('Invalid ecommerce product: ' + JSON.stringify(product));
            return false;
        }

        if(!Object.prototype.hasOwnProperty.call(product, 'sku')) {
            logConsoleError('Missing required SKU property in ecommerce product: ' + JSON.stringify(product) + '.');
            return false;
        }

        const skuType = typeof product.sku;

        if (skuType !== 'string' && skuType !== 'number') {
          logConsoleError(
            `Product.sku has to be of type string or number. Received: ${skuType}. In product: ${JSON.stringify(product)}`
          );
          return false;
        }

        if(Object.prototype.hasOwnProperty.call(product, 'customDimensions') && !isObject(product.customDimensions)) {
            logConsoleError('Invalid "customDimensions" property. Expected Object type, but got: ' + JSON.stringify(product) + '.');
            return false;
        }

        return true;
    }

    /**
     * @param {string} eventType
     * @param {Product[]} products
     * @param {Omit<PaymentInformation, 'orderId'> & {orderId?: string | number}} [paymentInformation]
     * @param {EcommerceOptions} [options]
     */
    function ecommerceTrackEvent(eventType, products, paymentInformation, options) {
        var request = '',
            lastEcommerceOrderTs,
            now = new Date(),
            items = [],
            i,
            orderId,
            grandTotal,
            subTotal,
            tax,
            shipping,
            discount;

        request += getActionNameParam('');
        request += '&e_t=' + eventType;

        if(options?.currencyCode){
            request += "&cur=" + options.currencyCode
        }

        if(paymentInformation) {
            orderId = paymentInformation.orderId;
            grandTotal = paymentInformation.grandTotal;
            subTotal = paymentInformation.subTotal;
            tax = paymentInformation.tax;
            shipping = paymentInformation.shipping;
            discount = paymentInformation.discount;

            if (orderId) {
                request += '&ec_id=' + encodeWrapper(orderId);
                // Record date of order in the visitor cookie
                lastEcommerceOrderTs = getUnixTimestamp(now);
            }

            request += '&revenue=' + grandTotal;

            if (isNumberRepresentation(subTotal)) {
                request += '&ec_st=' + subTotal;
            }

            if (isNumberRepresentation(tax)) {
                request += '&ec_tx=' + tax;
            }

            if (isNumberRepresentation(shipping)) {
                request += '&ec_sh=' + shipping;
            }

            if (isNumberRepresentation(discount)) {
                request += '&ec_dt=' + discount;
            }
        }

        if (products) {
            for (i = 0; i < products.length; i += 1) {
                // Ensure name and category default to healthy value
                if (!isDefined(products[i].name)) {
                    products[i].name = "";
                }

                if (!isDefined(products[i].category)) {
                    products[i].category = "";
                } else if(products[i].category instanceof Array) {
                    var categoryIndex;
                    // @ts-expect-error FIXME someday
                    for(categoryIndex = 0; categoryIndex < products[i].category.length; categoryIndex += 1) {
                        // @ts-expect-error FIXME someday
                        if(!isString(products[i].category[categoryIndex])) {
                            // @ts-expect-error FIXME someday
                            products[i].category[categoryIndex] = '';
                        }
                    }
                }

                // Set price to zero
                if (!isDefined(products[i].price) || String(products[i].price).length === 0) {
                    products[i].price = 0;
                }

                // Set quantity to 1
                if (!isDefined(products[i].quantity) || String(products[i].quantity).length === 0) {
                    products[i].quantity = 1;
                }

                // Set default brand
                if (!isDefined(products[i].brand) || String(products[i].brand).length === 0) {
                    products[i].brand = '';
                }

                // Set default variant
                if (!isDefined(products[i].variant) || String(products[i].variant).length === 0) {
                    products[i].variant = '';
                }

                // Set default variant
                if (!isDefined(products[i].customDimensions)) {
                    products[i].customDimensions = {};
                }

                items.push(removeEmptyElementsFromTheArrayRight([
                    String(products[i].sku),
                    String(products[i].name),
                    products[i].category,
                    products[i].price,
                    products[i].quantity,
                    products[i].brand,
                    String(products[i].variant),
                    products[i].customDimensions,
                ]));
            }
            request += '&ec_products=' + encodeWrapper(JSON.stringify(items));
        }
        request = getRequest(request, configCustomData, 'ecommerce', lastEcommerceOrderTs);
        sendRequest(request, configTrackerPause);
    }

    /**
     * @param {Product[]} products
     * @returns {boolean}
     */
    function ecommerceValidateProducts(products) {
        var i,
            valid = true;

        if(products instanceof Array) {
            for(i = 0; i < products.length; i += 1) {
                valid = valid && ecommerceValidateProduct(products[i]);
            }

            return valid;
        }

        logConsoleError(
            'Invalid ecommerce products. Expected value: array of objects. Recieved: ' +
            JSON.stringify(products) + '.'
        );

        return false
    }

    /**
     * @param {PaymentInformation} paymentInformation
     * @param {(keyof PaymentInformation)[]} requiredAttributes
     * @returns {boolean}
     */
    function ecommerceValidatePaymentInformation(paymentInformation, requiredAttributes) {
        var i, attributeName, attributeValue;

        if(typeof paymentInformation !== 'object' || paymentInformation === null) {
            logConsoleError(
                'Invalid paymentInformation. Expected object. Recieved: ' + JSON.stringify(paymentInformation) + '.'
            );

            return false;
        }

        for(i = 0; i < requiredAttributes.length; i += 1) {
            attributeName = requiredAttributes[i];
            attributeValue = paymentInformation[attributeName];

            if(attributeName === 'orderId')
            {
                if(!isDefined(attributeValue) || String(attributeValue) === '') {
                    logConsoleError(
                        'Invalid paymentInformation. Expected object containing "' + attributeName + '" attribute. ' +
                        'Recieved: ' + JSON.stringify(paymentInformation) + '.'
                    );

                    return false;
                }
            } else if(!isNumberRepresentation(attributeValue)) {
                logConsoleError(
                    'Invalid paymentInformation. Expected object containing [number] "' + attributeName + '" attribute. ' +
                    'Recieved: ' + JSON.stringify(paymentInformation) + '.'
                );

                return false;
            }
        }

        return true;
    }

    /**
     * @param {Product[]} products 
     * @param {number | string} grandTotal 
     * @param {EcommerceOptions} [options] 
     */
    function ecommerceCartUpdate(products, grandTotal, options) {
        if (!validateEcommerceOptionsParameter(options, 'ecommerceCartUpdate')) {
            options = undefined;
        }

        if(!isNumberRepresentation(grandTotal)) {
            logConsoleError('ecommerceCartUpdate requires [number] "grandTotal" parameter. ' +
            'Recieved: ' + JSON.stringify(grandTotal) + '.');

            return;
        }

        if(ecommerceValidateProducts(products)) {
            ecommerceTrackEvent('cart-update', products, { grandTotal }, options);
        }
    }

    /**
     * @param {Product[]} products 
     * @param {PaymentInformation} paymentInformation 
     * @param {EcommerceOptions} [options] 
     */
    function ecommerceOrder(products, paymentInformation, options) {
        if (!validateEcommerceOptionsParameter(options, "ecommerceOrder")) {
            options = undefined
          }

        if(ecommerceValidateProducts(products) && ecommerceValidatePaymentInformation(paymentInformation, ['orderId', 'grandTotal'])) {
            ecommerceTrackEvent('order', products, paymentInformation, options);
        }
    }

    function ecommerceTransformProducts() {
        var product, sku,
            items = [];
        if (ecommerceItems) {
            // Removing the SKU index in the array before JSON encoding
            for (sku in ecommerceItems) {
                if (Object.prototype.hasOwnProperty.call(ecommerceItems, sku)) {
                    product = {
                        sku: ecommerceItems[sku][0],
                        name: ecommerceItems[sku][1],
                        category: ecommerceItems[sku][2],
                        price: ecommerceItems[sku][3],
                        quantity: ecommerceItems[sku][4]
                    };

                    items.push(product);
                }
            }

            return items;
        }

        return [];
    }

    /**
     * 
     * @param {number | string} orderId 
     * @param {number | string} grandTotal 
     * @param {number | string} [subTotal] 
     * @param {number | string} [tax] 
     * @param {number | string} [shipping] 
     * @param {number | string} [discount] 
     */
    function logEcommerceOrder(orderId, grandTotal, subTotal, tax, shipping, discount) {
        if (String(orderId).length && isDefined(grandTotal)) {
            ecommerceOrder(
                ecommerceTransformProducts(),
                {
                    orderId,
                    grandTotal,
                    subTotal,
                    tax,
                    shipping,
                    discount
                }
            );
            ecommerceItems = {};
        }
    }

    /**
     * @param {number} grandTotal 
     */
    function logEcommerceCartUpdate(grandTotal) {
        ecommerceCartUpdate(ecommerceTransformProducts(), grandTotal);
    }

    /**
     * @param {string} [customTitle]
     * @returns {string}
     */
    function getActionNameParam(customTitle) {
        return 'action_name=' + encodeWrapper(titleFixup(customTitle || configTitle));
    }

    /**
     * Log the page view / visit
     * 
     * @param {string} [customTitle]
     * @param {Record<string, string>} [customData]
     * @param {() => void} [callback]
     */
    function logPageView(customTitle, customData, callback) {
        configIdPageView = generateUniqueId();

        var request = getRequest(getActionNameParam(customTitle), customData, 'log');

        sendRequest(request, configTrackerPause, callback);
    }

    /**
     * @param {string} interaction
     * @param {string} name
     * @param {string} piece
     * @param {string} target
     * @returns {string | undefined}
     */
    function buildContentInteractionRequest(interaction, name, piece, target)
    {
        var params = content.buildInteractionRequestParams(interaction, name, piece, target);

        if (!params) {
            return;
        }

        return getRequest(`${params}&${getActionNameParam()}`, null, 'contentInteraction');
    }

    /**
     * @param {HTMLElement | undefined} contentNode
     * @param {HTMLElement | undefined} interactedNode
     */
    function isNodeAuthorizedToTriggerInteraction(contentNode, interactedNode)
    {
        if (!contentNode || !interactedNode) {
            return false;
        }

        /** @type {HTMLElement[] | undefined} */
        let targetNodes = content.findTargetNodes(contentNode), targetNode;

        for(let i = 0; i < targetNodes.length; i++){
            targetNode = targetNodes[i];
            if (containsNodeElement(targetNode, interactedNode) && content.shouldIgnoreInteraction(targetNode)) {
                // interaction should be ignored
                return false;
            }

        }

        targetNodes = content.findTargetNodesNoDefault(contentNode)
        if(!targetNodes || targetNodes.length === 0) return true

        for(let i = 0; i < targetNodes.length; i++){
            targetNode = targetNodes[i]
            /**
             * There is a target node defined but the clicked element is not within the target node. example:
             * <div data-track-content>
             *  <a href="Y" data-content-target>Y</a>
             *  <img src=""/>
             *  <a href="Z">Z</a>
             * </div>
             *
             * The user clicked in this case on link Z and not on target Y
             */
            if (containsNodeElement(targetNode, interactedNode)) {
                return true;
            }

        }

        return false;
    }

    /**
     * 
     * @param {HTMLElement | undefined} anyNode 
     * @param {string} interaction 
     * @param {string} [fallbackTarget]
     * @returns {string | undefined}
     */
    function getContentInteractionToRequestIfPossible (anyNode, interaction, fallbackTarget)
    {
        if (!anyNode) {
            return;
        }

        var contentNode = content.findParentContentNode(anyNode);

        if (!contentNode) {
            // we are not within a content block
            return;
        }

        if (!isNodeAuthorizedToTriggerInteraction(contentNode, anyNode)) {
            return;
        }

        var contentBlock = content.buildContentBlock(contentNode);

        if (!contentBlock) {
            return;
        }

        if (!contentBlock.target && fallbackTarget) {
            contentBlock.target = fallbackTarget;
        }

        return content.buildInteractionRequestParams(interaction, contentBlock.name, contentBlock.piece, contentBlock.target);
    }

    /**
     * @param {{ name: string, piece: string, target: string }} contentBlock 
     * @returns {boolean}
     */
    function wasContentImpressionAlreadyTracked(contentBlock)
    {
        if (trackedContentImpressions.length === 0) {
            return false;
        }

        var index, trackedContent;

        for (index = 0; index < trackedContentImpressions.length; index++) {
            trackedContent = trackedContentImpressions[index];

            if (trackedContent &&
                trackedContent.name === contentBlock.name &&
                trackedContent.piece === contentBlock.piece &&
                trackedContent.target === contentBlock.target) {
                return true;
            }
        }

        return false;
    }

    /**
     * @param {HTMLElement} targetNode 
     * @returns {EventListener}
     */
    function trackContentImpressionClickInteraction (targetNode)
    {
        return function (event) {

            if (!targetNode) {
                return;
            }

            var contentBlock = content.findParentContentNode(targetNode);

            var interactedElement;
            if (event) {
                interactedElement = event.target || event.srcElement;
            }
            if (!interactedElement) {
                interactedElement = targetNode;
            }

            if (!isNodeAuthorizedToTriggerInteraction(contentBlock, /** @type {HTMLElement} */(interactedElement))) {
                return;
            }

            if (configRequestMethod !== 'beacon') {
                setExpireDateTime(configTrackerPause);
            }

            if (query.isLinkElement(targetNode) &&
                query.hasNodeAttributeWithValue(targetNode, 'href') &&
                query.hasNodeAttributeWithValue(targetNode, content.CONTENT_TARGET_ATTR)) {
                // there is a href attribute, the link was replaced with ppms.php but later the href was changed again by the application.
                var href = query.getAttributeValueFromNode(targetNode, 'href');
                if (!startsUrlWith(href || '', configTrackerUrl)) {
                    query.setAnyAttribute(targetNode, content.CONTENT_TARGET_ATTR, '');
                }
            }

            var link = getLinkIfShouldBeProcessed(targetNode, content, {
                configTrackerUrl,
                configDownloadClasses,
                configLinkClasses,
                configDownloadExtensions,
                configHostsAlias
            });

            if (linkTrackingInstalled && link && link.type) {
                // click ignore, will be tracked via processClick, we do not want to track it twice

                return link.type;
            }

            var block = content.buildContentBlock(contentBlock);

            if (!block) {
                return;
            }

            var contentName   = block.name;
            var contentPiece  = block.piece;
            var contentTarget = query.getAttributeValueFromNode(targetNode, content.CONTENT_TARGET_ATTR) || block.target;

            // click on any non link element, or on a link element that has not an href attribute or on an anchor
            var request = buildContentInteractionRequest('click', contentName, contentPiece, contentTarget);
            if(!request) return;

            sendRequest(`${request}&${getActionNameParam()}`, configTrackerPause);

            return request;
        };
    }

    /**
     * @param {HTMLElement[] | undefined} contentNodes 
     */
    function setupInteractionsTracking(contentNodes)
    {
        if (!contentNodes || !contentNodes.length) {
            return;
        }

        var contentIdx, targetNodes, targetNode, targetIdx
        for (contentIdx = 0; contentIdx < contentNodes.length; contentIdx++) {
            targetNodes = content.findTargetNodes(contentNodes[contentIdx]);

            for (targetIdx = 0; targetIdx < targetNodes.length; targetIdx++){
                targetNode = targetNodes[targetIdx]
                // @ts-expect-error this is our custom attribute
                if (targetNode && !targetNode.contentInteractionTrackingSetupDone) {
                    // @ts-expect-error this is our custom attribute
                    targetNode.contentInteractionTrackingSetupDone = true;

                    addEventListenerWrapper(targetNode, 'click', trackContentImpressionClickInteraction(targetNode));
                }
            }

        }
    }

    /**
     * @param {{name: string, piece: string, target: string}[] | undefined} contents
     * @param {HTMLElement[] | undefined} contentNodes
     */
    function buildContentImpressionsRequests(contents, contentNodes)
    {
        if (!contents || !contents.length) {
            return [];
        }

        var index, request;

        for (index = 0; index < contents.length; index++) {

            if (wasContentImpressionAlreadyTracked(contents[index])) {
                contents.splice(index, 1);
                index--;
            } else {
                trackedContentImpressions.push(contents[index]);
            }
        }

        if (!contents || !contents.length) {
            return [];
        }

        setupInteractionsTracking(contentNodes);

        var requests = [];

        for (index = 0; index < contents.length; index++) {

            request = getRequest(
                `${content.buildImpressionRequestParams(
                    contents[index].name,
                    contents[index].piece,
                    contents[index].target)}&${getActionNameParam()}`,
                undefined,
                'contentImpressions'
            );

            if (request) {
                requests.push(request);
            }
        }

        return requests;
    }

    /**
     * @param {HTMLElement[]} contentNodes 
     */
    function getContentImpressionsRequestsFromNodes(contentNodes)
    {
        var contents = content.collectContent(contentNodes);

        return buildContentImpressionsRequests(contents, contentNodes);
    }

    /**
     * @param {HTMLElement[] | undefined} contentNodes
     */
    function getCurrentlyVisibleContentImpressionsRequestsIfNotTrackedYet(contentNodes)
    {
        if (!contentNodes || !contentNodes.length) {
            return [];
        }

        var index;

        for (index = 0; index < contentNodes.length; index++) {
            if (!content.isNodeVisible(contentNodes[index])) {
                contentNodes.splice(index, 1);
                index--;
            }
        }

        if (!contentNodes || !contentNodes.length) {
            return [];
        }

        return getContentImpressionsRequestsFromNodes(contentNodes);
    }

    /**
     * 
     * @param {string} contentName 
     * @param {string} contentPiece 
     * @param {string} contentTarget 
     * @returns {string}
     */
    function buildContentImpressionRequest(contentName, contentPiece, contentTarget)
    {
        var params = content.buildImpressionRequestParams(contentName, contentPiece, contentTarget);

        return getRequest(`${params}&${getActionNameParam()}`, null, 'contentImpression');
    }

    /**
     * 
     * @param {HTMLElement | undefined} node 
     * @param {string} [contentInteraction] 
     * @returns 
     */
    function buildContentInteractionRequestNode(node, contentInteraction)
    {
        if (!node) {
            return;
        }

        var contentNode  = content.findParentContentNode(node);
        var contentBlock = content.buildContentBlock(contentNode);

        if (!contentBlock) {
            return;
        }

        if (!contentInteraction) {
            contentInteraction = 'Unknown';
        }

        return buildContentInteractionRequest(contentInteraction, contentBlock.name, contentBlock.piece, contentBlock.target);
    }

    /**
     * 
     * @param {string} category 
     * @param {string} action 
     * @param {string} [name] 
     * @param {number | string} [value] 
     * @returns 
     */
    function buildEventRequest(category, action, name, value)
    {
        return 'e_c=' + encodeWrapper(category)
             + '&e_a=' + encodeWrapper(action)
             + (isDefined(name) ? '&e_n=' + encodeWrapper(name) : '')
             + (isDefined(value) ? '&e_v=' + encodeWrapper(value) : '');
    }


    /**
     * @param {string} category 
     * @param {string} action 
     * @param {string} [name] 
     * @param {number | string} [value] 
     * @param {Record<string, string>} [customData] 
     * @param {() => void} [callback] 
     * @returns 
     */
    function logEvent(category, action, name, value, customData, callback)
    {
        // Category and Action are required parameters
        if (trim(String(category)).length === 0 || trim(String(action)).length === 0) {
            logConsoleError('Error while logging event: Parameters `category` and `action` must not be empty or filled with whitespaces');
            return false;
        }
        var request = getRequest(
                buildEventRequest(category, action, name, value) + '&' + getActionNameParam(''),
                customData,
                'event'
            );

        sendRequest(request, configTrackerPause, callback);
    }

    /**
     * @param {string} keyword
     * @param {string | string[] | null | false | undefined} category
     * @param {string | number | undefined} resultsCount
     * @param {Record<string, string> | undefined} customData
     */
    function logSiteSearch(keyword, category, resultsCount, customData) {
        var request = getRequest(
            'search=' + encodeWrapper(keyword)
            + (category
                ? '&search_cats=' + encodeWrapper(JSON.stringify(isString(category) ? [category] : category))
                : ''
            )
            + (isDefined(resultsCount) ? '&search_count=' + resultsCount : ''),
            customData,
            'sitesearch'
        );

        sendRequest(request, configTrackerPause);
    }

    /**
     * @param {string | number} idGoal
     * @param {number | undefined} conversionValue
     * @param {{} | undefined} customDimensions
     * @param {EcommerceOptions} options
     */
    function logGoal(idGoal, conversionValue, customDimensions, options) {
      var request = getRequest(
        'idgoal=' +
          idGoal +
          (conversionValue ? '&revenue=' + conversionValue : '') +
          '&' +
          getActionNameParam('') +
          (options?.currencyCode ? '&cur=' + options.currencyCode : ''),
        customDimensions,
        'goal'
      );

      sendRequest(request, configTrackerPause);
    }

    /**
     * Log the link or click with the server
     * 
     * @param {string} url 
     * @param {string} linkType 
     * @param {Record<string, string> | null} [customData] 
     * @param {(() => void) | null}[ callback] 
     * @param {HTMLElement} [sourceElement] 
     */
    function logLink(url, linkType, customData, callback, sourceElement) {
        let linkParams = `${getActionNameParam()}&${linkType}=${encodeWrapper(purify(url, { discardHashTag: configDiscardHashTag, visitorIdUrlParameter: configVisitorIdUrlParameter }))}`;

        var interaction = getContentInteractionToRequestIfPossible(sourceElement, 'click', url);

        if (interaction) {
            linkParams += '&' + interaction;
        }

        var request = getRequest(linkParams, customData, 'link');

        sendRequest(request, configTrackerPause, callback, true, 'beacon');
    }

    /**
     * Check for pre-rendered web pages, and log the page view/link/goal
     * according to the configuration and/or prerendering change
     * 
     * @param {() => void} callback 
     */
    function trackCallback(callback) {
      if (configCountPreRendered || !('prerendering' in documentAlias)) {
        callback();
        return;
      }

      // if pre-rendered, then defer callback
      if (documentAlias.prerendering) {
        documentAlias.addEventListener('prerenderingchange', function ready() {
          documentAlias.removeEventListener('prerenderingchange', ready);
          callback();
        });

        return;
      }

      callback();
    }

    /**
     * Process clicks
     * 
     * @param {HTMLElement} sourceElement 
     */
    function processClick(sourceElement) {
        if (crossDomainTrackingEnabled) {
            const linkElement = getSourceElement(sourceElement);

            if (isLinkToMobileAppInDomains(linkElement, configHostsAlias)) {
              replaceHrefForCrossDomainLink(
                linkElement,
                String(getValuesFromVisitorIdCookie().uuid),
                {
                  configVisitorIdUrlParameter,
                  configTrackerUrl,
                  browserFeatures,
                  crossDomainTrackingLinkDecorator
                }
              );
              return;
            }
          }

        var link = getLinkIfShouldBeProcessed(sourceElement, content, {
            configTrackerUrl,
            configDownloadClasses,
            configLinkClasses,
            configDownloadExtensions,
            configHostsAlias
        });

        // not a link to same domain or the same website (as set in setDomains())
        if (link && link.type) {
            logLink(link.href, link.type, undefined, null, sourceElement);
            return;
        }


        // a link to same domain or the same website (as set in setDomains())
        if (crossDomainTrackingEnabled) {
            // in case the clicked element is within the <a> (for example there is a <div> within the <a>) this will get the actual <a> link element
            sourceElement = getSourceElement(sourceElement);

            if(isLinkToDifferentDomainButSamePiwikWebsite(sourceElement, content, { hostAliases: configHostsAlias, domainAlias })) {
                replaceHrefForCrossDomainLink(
                  sourceElement,
                  String(getValuesFromVisitorIdCookie().uuid),
                  {
                    configVisitorIdUrlParameter,
                    configTrackerUrl,
                    browserFeatures,
                    crossDomainTrackingLinkDecorator
                  }
                );
            }

        }
    }

    // TODO: PPI-1422 - it can be removed
    function isIE8orOlder()
    {
        return documentAlias.all && !documentAlias.addEventListener;
    }

    /**
     * @param {MouseEvent} event 
     */
    function getKeyCodeFromEvent(event)
    {
        // event.which is deprecated https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/which
        var which = event.which;

        /**
         1 : Left mouse button
         2 : Wheel button or middle button
         3 : Right mouse button
         */

        var typeOfEventButton = (typeof event.button);

        if (!which && typeOfEventButton !== 'undefined' ) {
            /**
             -1: No button pressed
             0 : Main button pressed, usually the left button
             1 : Auxiliary button pressed, usually the wheel button or themiddle button (if present)
             2 : Secondary button pressed, usually the right button
             3 : Fourth button, typically the Browser Back button
             4 : Fifth button, typically the Browser Forward button

             IE8 and earlier has different values:
             1 : Left mouse button
             2 : Right mouse button
             4 : Wheel button or middle button

             For a left-hand configured mouse, the return values are reversed. We do not take care of that.
             */

            //  TODO: PPI-1422 - it can be removed
            if (isIE8orOlder()) {
                if (event.button & 1) {
                    which = 1;
                } else if (event.button & 2) {
                    which = 3;
                } else if (event.button & 4) {
                    which = 2;
                }
            } else {
                // @ts-expect-error browser compatibility
                if (event.button === 0 || event.button === '0') {
                    which = 1;
                } else if (event.button & 1) {
                    which = 2;
                } else if (event.button & 2) {
                    which = 3;
                }
            }
        }

        return which;
    }

    /**
     * @param {MouseEvent} event 
     */
    function getNameOfClickedButton(event)
    {
        switch (getKeyCodeFromEvent(event)) {
            case 1:
                return 'left';
            case 2:
                return 'middle';
            case 3:
                return 'right';
        }
    }

    /**
     *
     * @param {MouseEvent} event
     * @returns
     */
    function getTargetElementFromEvent(event)
    {
        return event.target || event.srcElement;
    }

    /**
     * Handle click event
     * 
     * @param {boolean} trackAlsoMiddleAndRightClicks 
     */
    function clickHandler(trackAlsoMiddleAndRightClicks) {
        /**
         * @param {MouseEvent} event
         */
        return function (event) {

            event = event || windowAlias.event;

            var button = getNameOfClickedButton(event);
            var target = getTargetElementFromEvent(event);

            if (event.type === 'click') {

                var ignoreClick = false;
                if (trackAlsoMiddleAndRightClicks && button === 'middle') {
                    // if enabled, we track middle clicks via mouseup
                    // some browsers (eg chrome) trigger click and mousedown/up events when middle is clicked,
                    // whereas some do not. This way we make "sure" to track them only once, either in click
                    // (default) or in mouseup (if enable == true)
                    ignoreClick = true;
                }

                if (target && !ignoreClick) {
                    processClick(/** @type {HTMLElement}*/ (target));
                }
            } else if (event.type === 'mousedown') {
                if (button === 'middle' && target) {
                    lastButton = button;
                    lastTarget = /** @type {HTMLElement}*/ (target);
                } else {
                    lastButton = lastTarget = null;
                }
            } else if (event.type === 'mouseup') {
                if (button === lastButton && target === lastTarget) {
                    processClick(/** @type {HTMLElement}*/ (target));
                }
                lastButton = lastTarget = null;
            } else if (event.type === 'contextmenu') {
                processClick(/** @type {HTMLElement}*/ (target));
            }
        };
    }

    /**
     * Add click listener to a DOM element
     * 
     * @param {HTMLElement} element
     * @param {boolean} [enable]
     */
    function addClickListener(element, enable) {
        var enableType = typeof enable;
        if (enableType === 'undefined') {
            enable = true;
        }

        addEventListenerWrapper(element, 'click', /** @type {EventListener}*/ (clickHandler(!!enable)), false);

        if (enable) {
            addEventListenerWrapper(element, 'mouseup', /** @type {EventListener}*/ (clickHandler(enable)), false);
            addEventListenerWrapper(element, 'mousedown', /** @type {EventListener}*/ (clickHandler(enable)), false);
            addEventListenerWrapper(element, 'contextmenu', /** @type {EventListener}*/ (clickHandler(enable)), false);
        }
    }

    /**
     * Returns closest parent anchor to the provided element
     *
     * @param {HTMLElement} element
     * @returns {HTMLElement | null}
     */
    function closestAnchor(element) {
        if (element.closest) {
            return element.closest('a');
        } else {
            while (element && element.tagName !== 'A') {
                element = /** @type {HTMLElement} */ (element.parentNode);
            }

            return element;
        }
    }

    /**
     * Add click handlers to anchor and AREA elements, except those to be ignored
     * 
     * @param {boolean} trackAlsoMiddleAndRightClicks
     */
    function addClickListeners(trackAlsoMiddleAndRightClicks) {
        /**
         * @param {PointerEvent} event
         */
        function anchorClickHandler(event) {
            if(!event.target) return;
            var linkElement = closestAnchor(/** @type {HTMLElement} */ (event.target));
            if (linkTrackingEnabled && linkElement && !getClassesRegExp(configIgnoreClasses, 'ignore').test(linkElement.className)) {
                clickHandler(trackAlsoMiddleAndRightClicks)(event);
            }
        }


        function removeClickListeners() {
            removeEventListener(document.body, 'click', /**@type {EventListener}*/ (anchorClickHandler), false);

            if (trackAlsoMiddleAndRightClicks) {
                removeEventListener(document.body, 'mouseup', /**@type {EventListener}*/ (anchorClickHandler), false);
                removeEventListener(document.body, 'mousedown', /**@type {EventListener}*/ (anchorClickHandler), false);
                removeEventListener(document.body, 'contextmenu', /**@type {EventListener}*/ (anchorClickHandler), false);
            }
        }

        if (!linkTrackingInstalled) {
            addEventListenerWrapper(document.body, 'click', /**@type {EventListener}*/ (anchorClickHandler), false);

            if (trackAlsoMiddleAndRightClicks) {
                addEventListenerWrapper(document.body, 'mouseup', /**@type {EventListener}*/ (anchorClickHandler), false);
                addEventListenerWrapper(document.body, 'mousedown', /**@type {EventListener}*/ (anchorClickHandler), false);
                addEventListenerWrapper(document.body, 'contextmenu', /**@type {EventListener}*/ (anchorClickHandler), false);
            }

            linkTrackingInstalled = removeClickListeners;
        }
    }

    /**
     * 
     * @param {boolean} checkOnScroll 
     * @param {number} timeIntervalInMs 
     * @param {Tracker} tracker 
     * @returns 
     */
    function enableTrackOnlyVisibleContent (checkOnScroll, timeIntervalInMs, tracker) {

        if (isTrackOnlyVisibleContentEnabled) {
            // already enabled, do not register intervals again
            return true;
        }

        isTrackOnlyVisibleContentEnabled = true;

        var didScroll = false;
        var events, index;

        function setDidScroll() { didScroll = true; }

        trackCallbackOnLoad(function () {

            /**
             * @param {number} intervalInMs 
             */
            function checkContent(intervalInMs) {
                setTimeout(function () {
                    if (!isTrackOnlyVisibleContentEnabled) {
                        return; // the tests stopped tracking only visible content
                    }
                    didScroll = false;
                    tracker.trackVisibleContentImpressions();
                    checkContent(intervalInMs);
                }, intervalInMs);
            }

            /**
             * @param {number} intervalInMs 
             */
            function checkContentIfDidScroll(intervalInMs) {

                setTimeout(function () {
                    if (!isTrackOnlyVisibleContentEnabled) {
                        return; // the tests stopped tracking only visible content
                    }

                    if (didScroll) {
                        didScroll = false;
                        tracker.trackVisibleContentImpressions();
                    }

                    checkContentIfDidScroll(intervalInMs);
                }, intervalInMs);
            }

            /**
             * @param {HTMLElement | null | undefined} element 
             * @returns {boolean}
             */
            function nodeIsVisible(element) {
                // Check if the element is null or undefined
                if (!element) return false;

                // Get the element's bounding client rect
                const boundingRect = element.getBoundingClientRect();

                // Check if the element has a positive width and height
                if (boundingRect.width <= 0 || boundingRect.height <= 0) return false;



                var style = null;

                // @ts-expect-error browser compatibility
                if(windowAlias.getComputedStyle) {
                    style = documentAlias.defaultView?.getComputedStyle(element,null);
                    // @ts-expect-error browser compatibility
                } else if(!style && element.currentStyle) {
                    // @ts-expect-error browser compatibility
                    style = element.currentStyle;
                } else if(!style) {
                    style = {}
                }

                if(style.opacity === '0' || style.display === 'none' || style.visibility === 'hidden') {
                    return false;
                }

                if(element.parentNode instanceof Element) {
                    return nodeIsVisible(/** @type {HTMLElement} */ (element.parentNode))
                }

                return true;
            }

            /**
             * @param {IntersectionObserverEntry[]} elements 
             */
            function intersectionCallback(elements) {
                /** @type {HTMLElement[]} */
                var contentNodes = [];
                var index;

                for(index = 0; index < elements.length; index += 1) {
                    if(elements[index].isIntersecting
                        && nodeIsVisible(/** @type {HTMLElement} */ (elements[index].target))
                        && content.isOrWasNodeInViewport(/** @type {HTMLElement} */ (elements[index].target))
                    ) {
                        contentNodes.push(/** @type {HTMLElement} */ (elements[index].target));
                    }
                }
                var requests = getContentImpressionsRequestsFromNodes(contentNodes);

                sendBulkRequest(requests, configTrackerPause);
            }

            function watchContentNodes() {
                if(windowAlias.IntersectionObserver) {
                    if(!visibleContentIntersectionObserver) {
                        visibleContentIntersectionObserver = new windowAlias.IntersectionObserver(intersectionCallback, {
                            threshold: 0
                        });
                    }

                    var contentNodes = content.findContentNodes();
                    // Add guard against multiple watches
                    for(index = 0; index < contentNodes.length; index += 1) {
                        if(!contentNodes[index].getAttribute(content.CONTENT_WATCHED_ATTR)) {
                            visibleContentIntersectionObserver.observe(contentNodes[index]);
                            contentNodes[index].setAttribute(content.CONTENT_WATCHED_ATTR, '1');
                        }
                    }

                    return true;
                }

                return false;
            }

            if(checkOnScroll || (timeIntervalInMs && timeIntervalInMs > 0)) {
                watchContentNodes();
            }

            if (checkOnScroll) {
                // scroll event is executed after each pixel, so we make sure not to
                // execute event too often. otherwise FPS goes down a lot!
                events = ['scroll', 'resize'];
                for (index = 0; index < events.length; index++) {
                    if (documentAlias.addEventListener) {
                        documentAlias.addEventListener(events[index], setDidScroll);
                    } else {
                        // @ts-expect-error browser compatibility
                        windowAlias.attachEvent('on' + events[index], setDidScroll);
                    }
                }

                checkContentIfDidScroll(100);
            }

            if (timeIntervalInMs && timeIntervalInMs > 0) {
                // @ts-expect-error unknown legacy
                timeIntervalInMs = parseInt(timeIntervalInMs, 10);
                checkContent(timeIntervalInMs);
            }

        });
    }

    /*
     * initialize tracker
     */
    browserFeatures = detectBrowserFeatures(cookiesService.hasCookies);
    updateDomainHash();


/*<DEBUG>*/
    /**
     * @fake-private
     */
    this.getQuery = function () {
        return query;
    };
    /**
     * @fake-private
     */
    this.getContent = function () {
        return content;
    };

    /**
     * @fake-private
     */
    this.buildContentImpressionRequest = buildContentImpressionRequest;
    /**
     * @fake-private
     */
    this.buildContentInteractionRequest = buildContentInteractionRequest;
    /**
     * @fake-private
     */
    this.buildContentInteractionRequestNode = buildContentInteractionRequestNode;
    /**
     * @fake-private
     */
    this.getContentImpressionsRequestsFromNodes = getContentImpressionsRequestsFromNodes;
    /**
     * @fake-private
     */
    this.getCurrentlyVisibleContentImpressionsRequestsIfNotTrackedYet = getCurrentlyVisibleContentImpressionsRequestsIfNotTrackedYet;
    this.trackCallbackOnLoad = trackCallbackOnLoad;
    this.trackCallbackOnReady = trackCallbackOnReady;
    /**
     * @fake-private
     */
    this.buildContentImpressionsRequests = buildContentImpressionsRequests;
    /**
     * @fake-private
     */
    this.wasContentImpressionAlreadyTracked = wasContentImpressionAlreadyTracked;
    /**
     * @fake-private
     */
    this.appendContentInteractionToRequestIfPossible = getContentInteractionToRequestIfPossible;
    /**
     * @fake-private
     */
    this.setupInteractionsTracking = setupInteractionsTracking;
    /**
     * @fake-private
     */
    this.trackContentImpressionClickInteraction = trackContentImpressionClickInteraction;
    /**
     * @fake-private
     */
    this.internalIsNodeVisible = isVisible;
    /**
     * @fake-private
     */
    this.isNodeAuthorizedToTriggerInteraction = isNodeAuthorizedToTriggerInteraction;
    this.getDomains = function () {
        return configHostsAlias;
    };
    this.getConfigIdPageView = function () {
        return configIdPageView;
    };
    this.getConfigDownloadExtensions = function () {
        return configDownloadExtensions;
    };
    /**
     * @fake-private
     * 
     * @param {boolean} checkOnScroll
     * @param {number} timeIntervalInMs
     */
    this.enableTrackOnlyVisibleContent = function (checkOnScroll, timeIntervalInMs) {
        return enableTrackOnlyVisibleContent(checkOnScroll, timeIntervalInMs, this);
    };
    /**
     * @fake-private
     */
    this.clearTrackedContentImpressions = function () {
        trackedContentImpressions = [];
    };
    /**
     * @fake-private
     */
    this.getTrackedContentImpressions = function () {
        return trackedContentImpressions;
    };
    /**
     * @fake-private
     */
    this.clearEnableTrackOnlyVisibleContent = function () {
        isTrackOnlyVisibleContentEnabled = false;
        visibleContentIntersectionObserver && visibleContentIntersectionObserver.disconnect();
        visibleContentIntersectionObserver = undefined;
    };
    this.disableLinkTracking = function () {
        if (typeof linkTrackingInstalled === 'function') {
            linkTrackingInstalled();
        }
        linkTrackingInstalled = false;
        linkTrackingEnabled   = false;
    };
    this.getConfigVisitorCookieTimeout = function () {
        return configVisitorCookieTimeout;
    };
    /**
     * @fake-private
     */
    this.removeAllAsyncTrackersButFirst = function () {
        var firstTracker = asyncTrackers[0];
        asyncTrackers = [firstTracker];
    };
/*</DEBUG>*/

    this.ping = function() {
        var requestPing = getRequest('ping=6', null, 'ping');
        sendRequest(requestPing, configTrackerPause);
    }

    /**
     * Get visitor ID (from first party cookie)
     *
     * @return string Visitor ID in hexits (or empty string, if not yet known)
     */
    this.getVisitorId = function () {
        return getValuesFromVisitorIdCookie().uuid;
    };

    /**
     * Get the visitor information (from first party cookie)
     *
     * @return array
     */
    this.getVisitorInfo = function () {
        // Note: in a new method, we could return also return getValuesFromVisitorIdCookie()
        //       which returns named parameters rather than returning integer indexed array
        return loadVisitorIdCookie();
    };

    /**
     * Specify the Piwik tracking URL
     *
     * @param {string} trackerUrl
     */
    this.setTrackerUrl = function (trackerUrl) {
        configTrackerUrl = trackerUrl;
    };

    /**
     * Returns the Piwik tracking URL
     * @returns string
     */
    this.getTrackerUrl = function () {
        return configTrackerUrl;
    };

    /**
     * Returns the Piwik server URL.
     * @deprecated Use getTrackerUrl() instead.
     * @returns string
     */
    this.getPiwikUrl = function () {
        return getPiwikUrlForOverlay(this.getTrackerUrl(), configApiUrl, content);
    };

    /**
     * Adds a new tracker. All sent requests will be also sent to the given siteId and piwikUrl.
     *
     * @param {string | null | undefined} piwikUrl  The tracker URL of the current tracker instance
     * @param {string} siteId
     * @return Tracker
     */
    this.addTracker = function (piwikUrl, siteId) {
        if (!siteId) {
            throw new Error('A siteId must be given to add a new tracker');
        }

        if (!isDefined(piwikUrl) || null === piwikUrl) {
            piwikUrl = this.getTrackerUrl();
        }

        var tracker = new Tracker(piwikUrl, siteId);

        asyncTrackers.push(tracker);

        return tracker;
    };

    /**
     * Returns the site ID
     *
     * @returns int
     */
    this.getSiteId = function() {
        return configTrackerSiteId;
    };

    /**
     * Specify the site ID
     *
     * @param int|string siteId
     */
    this.setSiteId = setSiteId;

    /**
     * Set Visitor ID Cookie (if it is not available already)
     */
    this.setVisitorIdCookie = function () {
        setVisitorIdCookie();
    };

    /**
     * Clears the User ID and generates a new visitor id.
     */
    this.resetUserId = function() {
        configUserId = '';
    };

    /**
     * Mark User as tracked anonymously
     *
     * @param {boolean} isAnonymous
     */
    this.setUserIsAnonymous = function (isAnonymous) {
        configUserIsAnonymous = isAnonymous;
    };

    /**
     * Changes setting of session ID strict mode. In strict mode tracker won't send information that can be used to
     * identify the browser (e.g. screen resolution or installed plugins)
     * @param {boolean} strict
     */
    this.setSessionIdStrictPrivacyMode = function (strict) {
        sessionIdStrictPrivacyMode = strict;
    };

    /**
     * Sets another another request triggered onLoad, after trackPageView setting timing data
     * Normally we try to use trackPageView as soon as possible, not to lose any actions
     * but since it's usually before the full page was loaded then we don't have complete data about timing.
     * You may either trigger trackPageView after onLoad event or enable this option so the followup request
     * containing all timing values is sent after onLoad.
     *
     * Argument to this function represents data sampling percentage (with possible integer values between 0 and 100).
     * Examples:
     * setTimingDataSamplingOnPageLoad(0) - disables timing data collection
     * setTimingDataSamplingOnPageLoad(5) - default value
     * setTimingDataSamplingOnPageLoad(30) - enables 30% data sampling (only around 30% of all tracked actions will collect timing data if possible)
     * setTimingDataSamplingOnPageLoad(100) - enables 100% data sampling (which means that all tracked actions will collect timing data if possible)
     * we recommend to use integers as an argument, because of e.g.
     * setTimingDataSamplingOnPageLoad(true) - enables only 1% data sampling, not 100% because boolean true is casted as integer 1
     *
     * IMPORTANT: by default this feature uses 10% data sampling
     * IMPORTANT: if enabled, timing data is collected only when page view lasted longer than the time it takse the page to load
     *            no partial information is stored, all metrics or nothing
     *
     * @param {number | boolean | string} updateTimingDataOnPageLoadSampling [>=0, <=100]
     */
    this.setTimingDataSamplingOnPageLoad = function (updateTimingDataOnPageLoadSampling) {
        if (updateTimingDataOnPageLoadSampling === true) {
            updateTimingDataOnPageLoadSampling = 1;
        }
        if (updateTimingDataOnPageLoadSampling === false) {
            updateTimingDataOnPageLoadSampling = 0;
        }
        updateTimingDataOnPageLoadSampling = parseInt(/** @type {string} */ (updateTimingDataOnPageLoadSampling), 10);
        if (updateTimingDataOnPageLoadSampling < 0) {
            updateTimingDataOnPageLoadSampling = 0;
        } else if (updateTimingDataOnPageLoadSampling > 100) {
            updateTimingDataOnPageLoadSampling = 100;
        }
        configUpdateTimingDataOnPageLoadSampling = updateTimingDataOnPageLoadSampling;
    };

    /**
     * Check current value of configUpdateTimingDataOnPageLoadSampling
     *
     * @returns integer configUpdateTimingDataOnPageLoadSampling
     */
    this.getTimingDataSamplingOnPageLoad = function () {
        return configUpdateTimingDataOnPageLoadSampling;
    };

    /**
     * Sets a User ID to this user (such as an email address or a username)
     *
     * @param {string} userId
     */
    this.setUserId = function (userId) {
        if(!isDefined(userId) || !userId.length) {
            return;
        }
        configUserId = userId;
    };

    /**
     * Gets the User ID if set.
     *
     * @returns string User ID
     */
    this.getUserId = function() {
        return configUserId;
    };

    /**
     * @fake-private
     * Pass custom data to the server
     *
     * Examples:
     *   tracker.setCustomData(object);
     *   tracker.setCustomData(key, value);
     *
     * @param {Record<string | number, string | number> | string} key_or_obj
     * @param {string | number} [opt_value]
     */
    this.setCustomData = function (key_or_obj, opt_value) {
        if (isObject(key_or_obj)) {
            configCustomData = key_or_obj;
        } else {
            if (!configCustomData) {
                configCustomData = {};
            }
            configCustomData[key_or_obj] = opt_value;
        }
    };

    /**
     * @fake-private
     * Get custom data
     * 
     * @returns {Record<string | number, string | number | undefined>}
     */
    this.getCustomData = function () {
        return configCustomData;
    };

    /**
     * Configure function with custom request content processing logic.
     * It gets called after request content in form of query parameters string has been prepared and before request content gets sent.
     *
     * Examples:
     *   tracker.setCustomRequestProcessing(function(request){
     *     var pairs = request.split('&');
     *     var result = {};
     *     pairs.forEach(function(pair) {
     *       pair = pair.split('=');
     *       result[pair[0]] = decodeURIComponent(pair[1] || '');
     *     });
     *     return JSON.stringify(result);
     *   });
     *
     * @param {(query: string) => string | undefined} customRequestProcessingFunction
     */
    this.setCustomRequestProcessing = function (customRequestProcessingFunction) {
        customRequestProcessor = customRequestProcessingFunction;
    };

    /**
     * Appends the specified query string to the ppms.php?... Tracking API URL
     *
     * @param {string} queryString eg. 'lat=140&long=100'
     */
    this.appendToTrackingUrl = function (queryString) {
        configAppendToTrackingUrl = queryString;
    };

    /**
     * Returns the query string for the current HTTP Tracking API request.
     * Piwik would prepend the hostname and path to Piwik: http://example.org/piwik/ppms.php?
     * prior to sending the request.
     *
     * @param {string} request eg. "param=value&param2=value2"
     */
    this.getRequest = function (request) {
        return getRequest(request);
    };

    /**
     * @deprecated
     * Set Custom Dimensions. Set Custom Dimensions will not be cleared after a tracked pageview and will
     * be sent along all following tracking requests. It is possible to remove/clear a value via `deleteCustomDimension`.
     *
     * @param {number | string} customDimensionId A Custom Dimension index
     * @param {string} value
     */
    this.setCustomDimension = function (customDimensionId, value) {
        customDimensionId = parseInt(/** @type {string} */ (customDimensionId), 10);
        if (customDimensionId > 0) {
            if (Object.prototype.hasOwnProperty.call(customDimensionsEncoded, customDimensionId)) {
                logConsoleWarn('Custom dimension ' + customDimensionId + ' was already set by "setCustomDimensionValue" method. Value set by "setCustomDimension" will be ignored.');
                return;
            }
            if (!isDefined(value)) {
                value = '';
            }
            if (!isString(value)) {
                value = String(value);
            }
            customDimensions[customDimensionId] = value;
        }
    };

    /**
     * @deprecated
     * Get a stored value for a specific Custom Dimension index.
     *
     * @param {number | string} customDimensionId A Custom Dimension index
     */
    this.getCustomDimension = function (customDimensionId) {
        customDimensionId = parseInt(/** @type {string} */ (customDimensionId), 10);
        if (customDimensionId > 0 && Object.prototype.hasOwnProperty.call(customDimensions, customDimensionId)) {
            return customDimensions[customDimensionId];
        }
    };

    /**
     * Delete a custom dimension.
     *
     * @param {number | string} customDimensionId Custom dimension Id
     */
    this.deleteCustomDimension = function (customDimensionId) {
        customDimensionId = parseInt(/** @type {string} */ (customDimensionId), 10);
        if (customDimensionId > 0) {
            delete customDimensions[customDimensionId];
            delete customDimensionsEncoded[customDimensionId];
        }
    };

    /**
     * Set Custom Dimensions. Set Custom Dimensions will not be cleared after a tracked pageview and will
     * be sent along all following tracking requests. It is possible to remove/clear a value via `deleteCustomDimension`.
     *
     * @param {number | string} customDimensionId A Custom Dimension index
     * @param {string | number} value
     */
    this.setCustomDimensionValue = function (customDimensionId, value) {
        customDimensionId = parseInt(/** @type {string} */ (customDimensionId), 10);
        if (customDimensionId > 0) {
            if (!isDefined(value)) {
                value = '';
            }
            if (!isString(value)) {
                value = String(value);
            }
            customDimensionsEncoded[customDimensionId] = value;
            delete customDimensions[customDimensionId];
        }
    };

    /**
     * Get a stored value for a specific Custom Dimension index.
     *
     * @param {number | string} customDimensionId A Custom Dimension index
     */
    this.getCustomDimensionValue = function (customDimensionId) {
        customDimensionId = parseInt(/** @type {string} */ (customDimensionId), 10);
        if (customDimensionId > 0 && Object.prototype.hasOwnProperty.call(customDimensionsEncoded, customDimensionId)) {
            return customDimensionsEncoded[customDimensionId];
        }
    };

    /**
     * Set custom variable within this visit
     *
     * @param {number} index Custom variable slot ID from 1-5
     * @param {string | number} name
     * @param {string | number} [value]
     * @param {string | number} [scope] Scope of Custom Variable:
     *                     - "visit" will store the name/value in the visit and will persist it in the cookie for the duration of the visit,
     *                     - "page" will store the name/value in the next page view tracked.
     *                     - "event" will store the name/value in the next event tracked.
     */
    this.setCustomVariable = function (index, name, value, scope) {
        if (!isDefined(scope)) {
            scope = 'visit';
        }
        if (!isDefined(name)) {
            return;
        }
        if (!isDefined(value)) {
            value = "";
        }
        if (index > 0) {
            name = !isString(name) ? String(name) : name;
            value = !isString(value) ? String(value) : value;
            /** @type {[string, string]} */
            const toRecord = [name.slice(0, customVariableMaximumLength), value.slice(0, customVariableMaximumLength)];
            // numeric scope is there for GA compatibility
            if (scope === 'visit' || scope === 2) {
                loadCustomVariables();
                customVariables[index] = toRecord;
            } else if (scope === 'page' || scope === 3) {
                customVariablesPage[index] = toRecord;
            } else if (scope === 'event') { /* GA does not have 'event' scope but we do */
                customVariablesEvent[index] = toRecord;
            }
        }
    };

    /**
     * Get custom variable
     *
     * @param {number} index Custom variable slot ID from 1-5
     * @param {string | number} [scope] Scope of Custom Variable: "visit" or "page" or "event"
     * @returns {[string, string] | false}
     */
    this.getCustomVariable = function (index, scope) {
        /** @type {[string, string] | undefined} */
        let cvar;

        if (!isDefined(scope)) {
            scope = "visit";
        }

        if (scope === "page" || scope === 3) {
            cvar = customVariablesPage[index];
        } else if (scope === "event") {
            cvar = customVariablesEvent[index];
        } else if (scope === "visit" || scope === 2) {
            loadCustomVariables();
            cvar = (customVariables)[index];
        }

        if (!isDefined(cvar) || (cvar && cvar[0] === '')) {
            return false;
        }

        return cvar;
    };

    /**
     * Delete custom variable
     *
     * @param {number} index Custom variable slot ID from 1-5
     * @param {string | number} [scope]
     */
    this.deleteCustomVariable = function (index, scope) {
        // Only delete if it was there already
        if (this.getCustomVariable(index, scope)) {
            this.setCustomVariable(index, '', '', scope);
        }
    };

    /**
     * @fake-private
     * Deletes all custom variables for a certain scope.
     *
     * @param {string | number} scope
     */
    this.deleteCustomVariables = function (scope) {
        if (scope === "page" || scope === 3) {
            customVariablesPage = {};
        } else if (scope === "event") {
            customVariablesEvent = {};
        } else if (scope === "visit" || scope === 2) {
            customVariables = {};
        }
    };

    /**
     * When called then the Custom Variables of scope "visit" will be stored (persisted) in a first party cookie
     * for the duration of the visit. This is useful if you want to call getCustomVariable later in the visit.
     *
     * By default, Custom Variables of scope "visit" are not stored on the visitor's computer.
     */
    this.storeCustomVariablesInCookie = function () {
        configStoreCustomVariablesInCookie = true;
    };

    /**
     * Set delay for link tracking (in milliseconds)
     *
     * @param {number} delay
     */
    this.setLinkTrackingTimer = function (delay) {
        configTrackerPause = delay;
    };

    /**
     * Get delay for link tracking (in milliseconds)
     */
    this.getLinkTrackingTimer = function () {
        return configTrackerPause;
    };

    /**
     * Set list of file extensions to be recognized as downloads
     *
     * @param {string | string[]} extensions
     */
    this.setDownloadExtensions = function (extensions) {
        if(isString(extensions)) {
            extensions = extensions.split('|');
        }
        configDownloadExtensions = extensions;
    };

    /**
     * Specify additional file extensions to be recognized as downloads
     *
     * @param {string | string[]} extensions  for example 'custom' or ['custom1','custom2','custom3']
     */
    this.addDownloadExtensions = function (extensions) {
        var i;
        if(isString(extensions)) {
            extensions = extensions.split('|');
        }
        for (i=0; i < extensions.length; i++) {
            configDownloadExtensions.push(extensions[i]);
        }
    };

    /**
     * Removes specified file extensions from the list of recognized downloads
     *
     * @param {string | string[]} extensions  for example 'custom' or ['custom1','custom2','custom3']
     */
    this.removeDownloadExtensions = function (extensions) {
        var i, newExtensions = [];
        if(isString(extensions)) {
            extensions = extensions.split('|');
        }
        for (i=0; i < configDownloadExtensions.length; i++) {
            if (indexOfArray(extensions, configDownloadExtensions[i]) === -1) {
                newExtensions.push(configDownloadExtensions[i]);
            }
        }
        configDownloadExtensions = newExtensions;
    };

    /**
     * Set array of domains to be treated as local. Also supports path, eg '.piwik.pro/subsite1'. In this
     * case all links that don't go to '*.piwik.pro/subsite1/ *' would be treated as outlinks.
     * For example a link to 'piwik.pro/' or 'piwik.pro/subsite2' both would be treated as outlinks.
     *
     * Also supports page wildcard, eg 'piwik.pro/index*'. In this case all links
     * that don't go to piwik.pro/index* would be treated as outlinks.
     *
     * The current domain will be added automatically if no given host alias contains a path and if no host
     * alias is already given for the current host alias. Say you are on "example.org" and set
     * "hostAlias = ['example.com', 'example.org/test']" then the current "example.org" domain will not be
     * added as there is already a more restrictive hostAlias 'example.org/test' given. We also do not add
     * it automatically if there was any other host specifying any path like
     * "['example.com', 'example2.com/test']". In this case we would also not add the current
     * domain "example.org" automatically as the "path" feature is used. As soon as someone uses the path
     * feature, for Piwik JS Tracker to work correctly in all cases, one needs to specify all hosts
     * manually.
     *
     * @param {string | string[]} hostsAlias
     */
    this.setDomains = function (hostsAlias) {
        configHostsAlias = isString(hostsAlias) ? [hostsAlias] : hostsAlias;

        let hasDomainAliasAlready = false;
        for (let i = 0; i < configHostsAlias.length; i++) {
            const alias = String(configHostsAlias[i]);

            if (isSameHost(domainAlias, domainFixup(alias))) {
                hasDomainAliasAlready = true;
                break;
            }

            const pathName = getPathName(alias, content);
            if (pathName && pathName !== '/' && pathName !== '/*') {
                hasDomainAliasAlready = true;
                break;
            }
        }

        // The current domain will be added automatically if no given host alias contains a path
        // and if no host alias is already given for the current host alias.
        if (!hasDomainAliasAlready) {
            /**
             * eg if domainAlias = 'piwik.pro' and someone set hostsAlias = ['piwik.pro/foo'] then we should
             * not add piwik.pro as it would increase the allowed scope.
             */
            configHostsAlias.push(domainAlias);
        }
    };

    /**
     * Enables cross domain linking. By default, the visitor ID that identifies a unique visitor is stored in
     * the browser's first party cookies. This means the cookie can only be accessed by pages on the same domain.
     * If you own multiple domains and would like to track all the actions and pageviews of a specific visitor
     * into the same visit, you may enable cross domain linking. Whenever a user clicks on a link it will append
     * a URL parameter pk_vid to the clicked URL which consists of these parts: 16 char visitorId, a 10 character
     * current timestamp and the last 6 characters are an id based on the userAgent to identify the users device).
     * This way the current visitorId is forwarded to the page of the different domain.
     *
     * On the different domain, the Piwik tracker will recognize the set visitorId from the URL parameter and
     * reuse this parameter if the page was loaded within 180 seconds. If cross domain linking was not enabled,
     * it would create a new visit on that page because we wouldn't be able to access the previously created
     * cookie. By enabling cross domain linking you can track several different domains into one website and
     * won't lose for example the original referrer.
     *
     * To make cross domain linking work you need to set which domains should be considered as your domains by
     * calling the method "setDomains()" first. We will add the URL parameter to links that go to a
     * different domain but only if the domain was previously set with "setDomains()" to make sure not to append
     * the URL parameters when a link actually goes to a third-party URL.
     */
    this.enableCrossDomainLinking = function () {
        crossDomainTrackingEnabled = true;
    };

    /**
     * Disable cross domain linking if it was previously enabled. See enableCrossDomainLinking();
     */
    this.disableCrossDomainLinking = function () {
        crossDomainTrackingEnabled = false;
    };

    /**
     * Detect whether cross domain linking is enabled or not. See enableCrossDomainLinking();
     * @returns {boolean}
     */
    this.isCrossDomainLinkingEnabled = function () {
        return crossDomainTrackingEnabled;
    };

    /**
     * Setts custom function used to decorate links. If return value is null - link won't change.
     * @param {(url: string, value: string, name: string) => string} decorator
     *  - url: link URL
     *  - value: value of visitor ID
     *  - name: parameter name of visitor ID
     */
    this.customCrossDomainLinkDecorator = function (decorator) {
        crossDomainTrackingLinkDecorator = decorator;
    };

    /**
     * Setts custom function used to fetch cross domain visitor ID from link
     * @param {(url: string, name: string) => string} visitorIdGetter
     *  - url: page URL
     *  - name: parameter name of visitor ID
     */
    this.customCrossDomainLinkVisitorIdGetter = function (visitorIdGetter) {
        crossDomainTrackingLinkVisitorIdGetter = visitorIdGetter;
    };

    /**
     * By default, the two visits across domains will be linked together
     * when the link is click and the page is loaded within 180 seconds.
     * @param {number} timeout in seconds
     */
    this.setCrossDomainLinkingTimeout = function (timeout) {
        configVisitorIdUrlParameterTimeoutInSeconds = timeout;
    };

    /**
     * Returns the query parameter appended to link URLs so cross domain visits
     * can be detected.
     *
     * If your application creates links dynamically, then you'll have to add this
     * query parameter manually to those links (since the JavaScript tracker cannot
     * detect when those links are added).
     *
     * Eg:
     *
     * var url = 'http://myotherdomain.com/?' + piwikTracker.getCrossDomainLinkingUrlParameter();
     * $element.append('<a href="' + url + '"/>');
     */
    this.getCrossDomainLinkingUrlParameter = function () {
        const visitorId = getValuesFromVisitorIdCookie().uuid
        return encodeWrapper(configVisitorIdUrlParameter) + '=' + encodeWrapper(getCrossDomainVisitorId(String(visitorId), browserFeatures));
    };

    /**
     * Set array of classes to be ignored if present in link
     *
     * @param {string | string[]} ignoreClasses
     */
    this.setIgnoreClasses = function (ignoreClasses) {
        configIgnoreClasses = isString(ignoreClasses) ? [ignoreClasses] : ignoreClasses;
    };

    /**
     * Set request method
     *
     * @param {string} method GET, POST or beacon; default is GET
     */
    this.setRequestMethod = function (method) {
        configRequestMethod = method || defaultRequestMethod;
    };

    /**
     * Set request Content-Type header value, applicable when POST request method is used for submitting tracking events.
     * See XMLHttpRequest Level 2 spec, section 4.7.2 for invalid headers
     * @link http://dvcs.w3.org/hg/xhr/raw-file/tip/Overview.html
     *
     * @param {string} requestContentType; default is 'application/x-www-form-urlencoded; charset=UTF-8'
     */
    this.setRequestContentType = function (requestContentType) {
        configRequestContentType = requestContentType || defaultRequestContentType;
    };

    /**
     * Override referrer
     *
     * @param {string} url
     */
    this.setReferrerUrl = function (url) {
        configReferrerUrl = url;
    };

    /**
     * Override url
     *
     * @param {string} url
     */
    this.setCustomUrl = function (url) {
      if (typeof url !== 'string') {
        logConsoleError(`[setCustomUrl]: url must be a string, got: ${JSON.stringify(url)}`);
        return;
      }

      configCustomUrl = resolveRelativeReference(locationHrefAlias, url, {
        discardHashTag: configDiscardHashTag,
        visitorIdUrlParameter: configVisitorIdUrlParameter,
      });
    };

    /**
     * Returns the current url of the page that is currently being visited. If a custom URL was set, the
     * previously defined custom URL will be returned.
     */
    this.getCurrentUrl = function () {
        return configCustomUrl || locationHrefAlias;
    };

    /**
     * Override document.title
     *
     * @param {string} title
     */
    this.setDocumentTitle = function (title) {
        configTitle = title;
    };

    /**
     * Set the URL of the Piwik API. It is used for Page Overlay.
     * This method should only be called when the API URL differs from the tracker URL.
     *
     * @param {string} apiUrl
     */
    this.setAPIUrl = function (apiUrl) {
        configApiUrl = apiUrl;
    };

    /**
     * Set array of classes to be treated as downloads
     *
     * @param {string | string[]} downloadClasses
     */
    this.setDownloadClasses = function (downloadClasses) {
        configDownloadClasses = isString(downloadClasses) ? [downloadClasses] : downloadClasses;
    };

    /**
     * Set array of classes to be treated as outlinks
     *
     * @param {string | string[]} linkClasses
     */
    this.setLinkClasses = function (linkClasses) {
        configLinkClasses = isString(linkClasses) ? [linkClasses] : linkClasses;
    };

    /**
     * Strip hash tag (or anchor) from URL
     * Note: this can be done in the Piwik>Settings>Websites on a per-website basis
     *
     * @deprecated
     * @param {boolean} enableFilter
     */
    this.discardHashTag = function (enableFilter) {
        configDiscardHashTag = enableFilter;
    };

    /**
     * Set first-party cookie name prefix
     *
     * @param {string} cookieNamePrefix
     */
    this.setCookieNamePrefix = function (cookieNamePrefix) {
        configCookieNamePrefix = cookieNamePrefix;
        // Re-init the Custom Variables cookie
        customVariables = getCustomVariablesFromCookie();
    };

    /**
     * Set first-party cookie domain
     *
     * @param {string} domain
     */
    this.setCookieDomain = function (domain) {
        var domainFixed = domainFixup(domain);

        configCookieDomain = domainFixed;
        updateDomainHash();
    };

    /**
     * Get first-party cookie domain
     */
    this.getCookieDomain = function () {
        return configCookieDomain;
    };

    /**
     * Detect if cookies are enabled and supported by browser.
     */
    this.hasCookies = function () {
        return '1' === cookiesService.hasCookies();
    };

    /**
     * @fake-private
     * Set a first-party cookie for the duration of the session.
     *
     * @param {string} cookieName
     * @param {string} cookieValue
     * @param {number} [msToExpire] Defaults to session cookie timeout
     */
    this.setSessionCookie = function (cookieName, cookieValue, msToExpire) {
        if (!cookieName) {
            throw new Error('Missing cookie name');
        }

        if (!isDefined(msToExpire)) {
            msToExpire = configSessionCookieTimeout;
        }

        configCookiesToDelete.push(cookieName);

        cookiesService.setCookie(cookiesService.getCookieName(cookieName), cookieValue, { msToExpire });
    };

    /**
     * @fake-private
     * Get first-party cookie value.
     *
     * Returns null if cookies are disabled or if no cookie could be found for this name.
     *
     * @param {string} cookieName
     */
    this.getCookie = function (cookieName) {
        var cookieValue = cookiesService.getCookie(cookiesService.getCookieName(cookieName));

        if (cookieValue === 0) {
            return null;
        }

        return cookieValue;
    };

    /**
     * Set first-party cookie path.
     *
     * @param {string} path
     */
    this.setCookiePath = function (path) {
        configCookiePath = path;
        updateDomainHash();
    };

    /**
     * Get first-party cookie path.
     */
    this.getCookiePath = function () {
        return configCookiePath;
    };

    /**
     * Set visitor cookie timeout (in seconds)
     * Defaults to 13 months (timeout=33955200)
     *
     * @param {number} timeout
     */
    this.setVisitorCookieTimeout = function (timeout) {
        configVisitorCookieTimeout = timeout * 1000;
    };

    /**
     * Set session cookie timeout (in seconds).
     * Defaults to 30 minutes (timeout=1800)
     *
     * @param {number} timeout
     */
    this.setSessionCookieTimeout = function (timeout) {
        configSessionCookieTimeout = timeout * 1000;
    };

    /**
     * Get session cookie timeout (in seconds).
     */
    this.getSessionCookieTimeout = function () {
        return configSessionCookieTimeout;
    };

    /**
     * @deprecated referral cookie mechanism has been removed
     */
    this.setReferralCookieTimeout = function () {};

    /**
    * Enable the Secure cookie flag on all first party cookies.
    * This should be used when your website is only available under HTTPS
    * so that all tracking cookies are always sent over secure connection.
    *
    * @param {boolean} enable
    */
    this.setSecureCookie = function (enable) {
        configCookieIsSecure = enable;
    };

    /**
     * Disables all cookies from being set
     *
     * Existing cookies will be deleted on the next call to track
     */
    this.disableCookies = function () {
        configCookiesDisabled = true;

        if (configTrackerSiteId) {
            deleteCookies();
        }
    };

    /**
     * Enables all cookies
     *
     * Cookies will be set on the next call to track
     */
    this.enableCookies = function () {
        configCookiesDisabled = false;
    };

    /**
     * One off cookies clearing. Useful to call this when you know for sure a new visitor is using the same browser,
     * it maybe helps to "reset" tracking cookies to prevent data reuse for different users.
     */
    this.deleteCookies = function () {
        deleteCookies();
    };

    /**
     * @param {boolean} enable
     */
    this.setSiteInspectorSetup = function (enable) {
        configSiteInspectorSetup = enable;
    };

    /**
     * Handle do-not-track requests
     *
     * @deprecated
     * @param {boolean} enable If true, don't track if user agent sends 'do-not-track' header
     */
    this.setDoNotTrack = function (enable) {
        // @ts-expect-error browser compatibility
        var dnt = navigatorAlias.doNotTrack || navigatorAlias.msDoNotTrack;
        configDoNotTrack = enable && (dnt === 'yes' || dnt === '1');

        // do not track also disables cookies and deletes existing cookies
        if (configDoNotTrack) {
            this.disableCookies();
        }
    };

    /**
     * Add click listener to a specific link element.
     * When clicked, Piwik will log the click automatically.
     *
     * @param {HTMLElement} element
     * @param {boolean} [enable] If false, do not use pseudo click-handler (middle click + context menu)
     */
    this.addListener = function (element, enable) {
        addClickListener(element, enable);
    };

    /**
     * Install link tracker.
     *
     * If you change the DOM of your website or web application you need to make sure to call this method
     * again so Piwik can detect links that were added newly.
     *
     * The default behaviour is to use actual click events. However, some browsers
     * (e.g., Firefox, Opera, and Konqueror) don't generate click events for the middle mouse button.
     *
     * To capture more "clicks", the pseudo click-handler uses mousedown + mouseup events.
     * This is not industry standard and is vulnerable to false positives (e.g., drag events).
     *
     * There is a Safari/Chrome/Webkit bug that prevents tracking requests from being sent
     * by either click handler.  The workaround is to set a target attribute (which can't
     * be "_self", "_top", or "_parent").
     *
     * @see https://bugs.webkit.org/show_bug.cgi?id=54783
     *
     * @param {boolean} trackAlsoMiddleAndRightClicks Defaults to true.
     *                    * If "true", use pseudo click-handler (treat middle click and open contextmenu as
     *                    left click). A right click (or any click that opens the context menu) on a link
     *                    will be tracked as clicked even if "Open in new tab" is not selected.
     *                    * If "false" (default), nothing will be tracked on open context menu or middle click.
     *                    The context menu is usually opened to open a link / download in a new tab
     *                    therefore you can get more accurate results by treat it as a click but it can lead
     *                    to wrong click numbers.
     */
    this.enableLinkTracking = function (trackAlsoMiddleAndRightClicks) {
        this.disableLinkTracking();
        if (typeof trackAlsoMiddleAndRightClicks === 'undefined') {
            trackAlsoMiddleAndRightClicks = true;
        }
        linkTrackingEnabled = true;

        trackCallback(function () {
            trackCallbackOnReady(function () {
                addClickListeners(trackAlsoMiddleAndRightClicks);
            });
        });
    };

    /**
     * Send error event to tracker
     *
     * @param {string | Event} message
     * @param {string} url
     * @param {number} lineNumber
     * @param {number} columnNumber
     */
    function logErrorEvent(message, url, lineNumber, columnNumber) {
        /** @type {string} */
        var limitedMessage;

        if (enableJSErrorTracking) {
            limitedMessage = String(message).substring(0, 16000);
            trackCallback(function () {
                var category = 'JavaScript Errors';

                var action = url + ':' + lineNumber;
                if (columnNumber) {
                    action += ':' + columnNumber;
                }

                var errorId = JSON.stringify({
                    category: category,
                    action: action,
                    message: limitedMessage,
                });
                if (!onlyUniqueJavaScriptErrors || indexOfArray(javaScriptErrors, errorId) === -1) {
                    javaScriptErrors.push(errorId);

                    logEvent(category, action, limitedMessage);
                }
            });
        }
    }

    /**
     * Enable tracking of uncatched JavaScript errors
     *
     * @param {boolean} [unique] When true only unique errors will be sent to tracker. Default: true.
     *
     * If enabled, uncaught JavaScript Errors will be tracked as an event by defining a
     * window.onerror handler. If a window.onerror handler is already defined we will make
     * sure to call this previously registered error handler after tracking the error.
     *
     * By default we return false in the window.onerror handler to make sure the error still
     * appears in the browser's console etc. Note: Some older browsers might behave differently
     * so it could happen that an actual JavaScript error will be suppressed.
     * If a window.onerror handler was registered we will return the result of this handler.
     *
     * Make sure not to overwrite the window.onerror handler after enabling the JS error
     * tracking as the error tracking won't work otherwise. To capture all JS errors we
     * recommend to include the Piwik JavaScript tracker in the HTML as early as possible.
     * If possible directly in <head></head> before loading any other JavaScript.
     */
    this.enableJSErrorTracking = function (unique) {
        if (!isDefined(unique)) {
            unique = true;
        }

        if (enableJSErrorTracking) {
            return;
        }

        enableJSErrorTracking = true;
        onlyUniqueJavaScriptErrors = unique;
        var onError = windowAlias.onerror;

        windowAlias.onerror = function (message, url, linenumber, column, error) {
            logErrorEvent(message, /** @type {string} */ (url), /** @type {number} */ (linenumber), /** @type {number} */ (column));

            if (onError) {
                return onError(message, url, linenumber, column, error);
            }

            return false;
        };
    };

    /**
     * Disable automatic performance tracking
     */
    this.disablePerformanceTracking = function () {
        configPerformanceTrackingEnabled = false;
    };

    /**
     * Set the server generation time.
     * If set, the browser's performance.timing API in not used anymore to determine the time.
     *
     * @param {number | string} generationTime
     */
    this.setGenerationTimeMs = function (generationTime) {
        configPerformanceGenerationTime = parseInt(/** @type {string} */ (generationTime), 10);
    };

    /**
     * Enable heartbeat
     * @param {number[] | undefined} timers
     */
    this.enableHeartBeatTimer = function (timers) {
      if (timers) {
        const result = validateHeartbeatTimersParam(timers);
        if (!result.isValid) {
          logConsoleError(result.error);
          return;
        }
      } else {
        timers = DEFAULT_HEARTBEAT_TIMINGS;
      }

      heartBeat.setTimings(timers);

      heartBeatShouldBeEnabled = true

      // if a tracking request has already been sent, start the heart beat timeout
      if (lastTrackerRequestTime !== null) {
        heartBeat.enable();
      } 
    };

    this.disableHeartBeatTimer = function(){
        heartBeat.disable();
    }

    /**
     * @deprecated
     * Frame buster
     */
    this.killFrame = function () {
        // @ts-expect-error deprecated
        if (windowAlias.location !== windowAlias.top.location) {
            // @ts-expect-error deprecated
            windowAlias.top.location = windowAlias.location;
        }
    };

    /**
     * Redirect if browsing offline (aka file: buster)
     *
     * @deprecated
     * @param {string} url Redirect to this URL
     */
    this.redirectFile = function (url) {
        if (windowAlias.location.protocol === 'file:') {
            // @ts-expect-error deprecated
            windowAlias.location = url;
        }
    };

    /**
     * Count sites in pre-rendered state
     *
     * @param {boolean} enable If true, track when in pre-rendered state
     */
    this.setCountPreRendered = function (enable) {
        configCountPreRendered = enable;
    };

    /**
     * 
     * @param {string | number} idGoal
     * @param {number} [conversionValue]
     * @param {Record<string, string>} [customDimensions]
     * @param {EcommerceOptions} [options]
     */
    this.trackGoal = function (idGoal, conversionValue, customDimensions, options) {
      if (!validateTrackGoalOptionsParameter(options, 'trackGoal')) {
        options = undefined;
      }

      trackCallback(function () {
        logGoal(idGoal, conversionValue, customDimensions, options);
      });
    };

    /**
     * Manually log a click from your own code
     *
     * @param {string} sourceUrl
     * @param {string} linkType
     * @param {Record<string, string>} [customData]
     * @param {() => void} [callback]
     */
    this.trackLink = function (sourceUrl, linkType, customData, callback) {
        trackCallback(function () {
            logLink(sourceUrl, linkType, customData, callback);
        });
    };

    /**
     * Get the number of page views that have been tracked so far within the currently loaded page.
     */
    this.getNumTrackedPageViews = function () {
        return numTrackedPageviews;
    };

    /**
     * NOTE: weird configuration, but we need to keep the backwards compatibility
     * @param {{lastHeartbeat?: boolean, blurHeartbeat?: boolean} | undefined} options
     */
    this.trackHeartBeat = function (options) {
        // TODO: PPI-1422 - it can be removed - it's for supporting older browsers
        var windowHasFocus = (!documentAlias.hasFocus || documentAlias.hasFocus());

        if (heartBeat.isEnabled() && windowHasFocus && shouldSendHeartBeat()) {
            /** @type {PingLevelType} */
            let pingLevel = PingLevel.PeriodicHeartbeat
            if(options?.lastHeartbeat){
                pingLevel = PingLevel.LastHeartbeat
            }
            if(options?.blurHeartbeat){
                pingLevel = PingLevel.BlurHeartbeat
            }

            heartBeatPingIfActivity(pingLevel);
        }
    };

    /**
     * Log visit to this page
     *
     * @param {string} [customTitle]
     * @param {Record<string, string>} [customData]
     * @param {() => void} [callback]
     */
    this.trackPageView = function (customTitle, customData, callback) {
      trackedContentImpressions = [];
      javaScriptErrors = [];

      trackCallback(function () {
        numTrackedPageviews++;
        logPageView(customTitle, customData, callback);

        if (heartBeatShouldBeEnabled && !heartBeat.isEnabled()) {
          heartBeat.enable();
        }
        if (
          numTrackedPageviews <= 1 &&
          configUpdateTimingDataOnPageLoadSampling >= Math.floor(Math.random() * 100 + 1)
        ) {
          trackCallbackOnLoad(function () {
            // this has to be run through setTimeout so it's fired after load event ends,
            // that seems to be the only way to grab domContentLoadedEventEnd
            setTimeout(function () {
              var requestPing = getRequest('ping=5', null, 'ping');
              sendRequest(requestPing, configTrackerPause);
            }, 0);
          });
        }
      });
    };

    /**
     * Scans the entire DOM for all content blocks and tracks all impressions once the DOM ready event has
     * been triggered.
     *
     * If you only want to track visible content impressions have a look at `trackVisibleContentImpressions()`.
     * We do not track an impression of the same content block twice if you call this method multiple times
     * unless `trackPageView()` is called meanwhile. This is useful for single page applications.
     */
    this.trackAllContentImpressions = function () {
        trackCallback(function () {
            trackCallbackOnReady(function () {
                // we have to wait till DOM ready
                var contentNodes = content.findContentNodes();
                var requests     = getContentImpressionsRequestsFromNodes(contentNodes);

                sendBulkRequest(requests, configTrackerPause);
            });
        });
    };

    /**
     * Scans the entire DOM for all content blocks as soon as the page is loaded. It tracks an impression
     * only if a content block is actually visible. Meaning it is not hidden and the content is or was at
     * some point in the viewport.
     *
     * If you want to track all content blocks have a look at `trackAllContentImpressions()`.
     * We do not track an impression of the same content block twice if you call this method multiple times
     * unless `trackPageView()` is called meanwhile. This is useful for single page applications.
     *
     * Once you have called this method you can no longer change `checkOnScroll` or `timeIntervalInMs`.
     *
     * If you do want to only track visible content blocks but not want us to perform any automatic checks
     * as they can slow down your frames per second you can call `trackVisibleContentImpressions()` or
     * `trackContentImpressionsWithinNode()` manually at  any time to rescan the entire DOM for newly
     * visible content blocks.
     * o Call `trackVisibleContentImpressions(false, 0)` to initially track only visible content impressions
     * o Call `trackVisibleContentImpressions()` at any time again to rescan the entire DOM for newly visible content blocks or
     * o Call `trackContentImpressionsWithinNode(node)` at any time to rescan only a part of the DOM for newly visible content blocks
     *
     * @param {boolean} [checkOnScroll=true] Optional, you can disable rescanning the entire DOM automatically
     *                                     after each scroll event by passing the value `false`. If enabled,
     *                                     we check whether a previously hidden content blocks became visible
     *                                     after a scroll and if so track the impression.
     *                                     Note: If a content block is placed within a scrollable element
     *                                     (`overflow: scroll`), we can currently not detect when this block
     *                                     becomes visible.
     * @param {number} [timeIntervalInMs=750] Optional, you can define an interval to rescan the entire DOM
     *                                     for new impressions every X milliseconds by passing
     *                                     for instance `timeIntervalInMs=500` (rescan DOM every 500ms).
     *                                     Rescanning the entire DOM and detecting the visible state of content
     *                                     blocks can take a while depending on the browser and amount of content.
     *                                     In case your frames per second goes down you might want to increase
     *                                     this value or disable it by passing the value `0`.
     */
    this.trackVisibleContentImpressions = function (checkOnScroll, timeIntervalInMs) {
        if (!isDefined(checkOnScroll)) {
            checkOnScroll = true;
        }

        if (!isDefined(timeIntervalInMs)) {
            timeIntervalInMs = 750;
        }

        enableTrackOnlyVisibleContent(checkOnScroll, timeIntervalInMs, this);

        if(!visibleContentIntersectionObserver) {
            trackCallback(function () {
                trackCallbackOnLoad(function () {
                    // we have to wait till CSS parsed and applied
                    var contentNodes = content.findContentNodes();
                    var requests     = getCurrentlyVisibleContentImpressionsRequestsIfNotTrackedYet(contentNodes);

                    sendBulkRequest(requests, configTrackerPause);
                });
            });
        }
    };

    /**
     * Tracks a content impression using the specified values. You should not call this method too often
     * as each call causes an XHR tracking request and can slow down your site or your server.
     *
     * @param {string} contentName  For instance "Ad Sale".
     * @param {string} [contentPiece='Unknown'] For instance a path to an image or the text of a text ad.
     * @param {string} [contentTarget] For instance the URL of a landing page.
     */
    this.trackContentImpression = function (contentName, contentPiece, contentTarget) {
        contentName = trim(contentName);
        contentPiece = trim(contentPiece || 'Unknown');
        contentTarget = trim(contentTarget || '');

        if (!contentName) {
            return;
        }

        trackCallback(function () {
            var request = buildContentImpressionRequest(contentName, contentPiece, contentTarget);
            sendRequest(request, configTrackerPause);
        });
    };

    /**
     * Scans the given DOM node and its children for content blocks and tracks an impression for them if
     * no impression was already tracked for it. If you have called `trackVisibleContentImpressions()`
     * upfront only visible content blocks will be tracked. You can use this method if you, for instance,
     * dynamically add an element using JavaScript to your DOM after we have tracked the initial impressions.
     *
     * @param {HTMLElement} domNode
     */
    this.trackContentImpressionsWithinNode = function (domNode) {
        if(!domNode) {
            return;
        }

        trackCallback(function () {
            if (isTrackOnlyVisibleContentEnabled) {
                trackCallbackOnLoad(function () {
                    // we have to wait till CSS parsed and applied
                    var contentNodes = content.findContentNodesWithinNode(domNode);

                    var requests = getCurrentlyVisibleContentImpressionsRequestsIfNotTrackedYet(contentNodes);
                    sendBulkRequest(requests, configTrackerPause);
                });
            } else {
                trackCallbackOnReady(function () {
                    // we have to wait till DOM ready
                    var contentNodes = content.findContentNodesWithinNode(domNode);

                    var requests = getContentImpressionsRequestsFromNodes(contentNodes);
                    sendBulkRequest(requests, configTrackerPause);
                });
            }
        });
    };

    /**
     * Tracks a content interaction using the specified values. You should use this method only in conjunction
     * with `trackContentImpression()`. The specified `contentName` and `contentPiece` has to be exactly the
     * same as the ones that were used in `trackContentImpression()`. Otherwise the interaction will not count.
     *
     * @param {string} contentInteraction The type of interaction that happened. For instance 'click' or 'submit'.
     * @param {string} contentName  The name of the content. For instance "Ad Sale".
     * @param {string} [contentPiece='Unknown'] The actual content. For instance a path to an image or the text of a text ad.
     * @param {string} [contentTarget] For instance the URL of a landing page.
     */
    this.trackContentInteraction = function (contentInteraction, contentName, contentPiece, contentTarget) {
        contentInteraction = trim(contentInteraction);
        contentName = trim(contentName);
        contentPiece = trim(contentPiece || 'Unknown');
        contentTarget = trim(contentTarget || '');

        if (!contentInteraction || !contentName) {
            return;
        }


        trackCallback(function () {
            var request = buildContentInteractionRequest(contentInteraction, contentName, contentPiece, contentTarget);
            if(!request) return;
            sendRequest(request, configTrackerPause);
        });
    };

    /**
     * Tracks an interaction with the given DOM node / content block.
     *
     * By default we track interactions on click but sometimes you might want to track interactions yourself.
     * For instance you might want to track an interaction manually on a double click or a form submit.
     * Make sure to disable the automatic interaction tracking in this case by specifying either the CSS
     * class `piwikContentIgnoreInteraction` or the attribute `data-content-ignoreinteraction`.
     *
     * @param {HTMLElement} domNode  This element itself or any of its parent elements has to be a content block
     *                         element. Meaning one of those has to have a `piwikTrackContent` CSS class or
     *                         a `data-track-content` attribute.
     * @param {string} [contentInteraction] The name of the interaction that happened. For instance 'click', 'formSubmit', 'DblClick', ...
     */
    this.trackContentInteractionNode = function (domNode, contentInteraction) {
        if (!domNode) {
            return;
        }

        trackCallback(function () {
            var request = buildContentInteractionRequestNode(domNode, contentInteraction);
            if(!request) return;
            sendRequest(request, configTrackerPause);
        });
    };

    /**
     * Useful to debug content tracking. This method will log all detected content blocks to console
     * (if the browser supports the console). It will list the detected name, piece, and target of each
     * content block.
     */
    this.logAllContentBlocksOnPage = function () {
        var contentNodes = content.findContentNodes();
        var contents = content.collectContent(contentNodes);
        // eslint-disable-next-line no-console
        console.log(contents);
    };

    /**
     * Records an event
     *
     * @param {string} category The Event Category (Videos, Music, Games...)
     * @param {string} action The Event's Action (Play, Pause, Duration, Add Playlist, Downloaded, Clicked...)
     * @param {string} [name] The Event's object Name (a particular Movie name, or Song name, or File name...)
     * @param {number | string} [value] The Event's value
     * @param {() => void} [callback]
     * @param {Record<string, string>} [customData]
     */
    this.trackEvent = function (category, action, name, value, customData, callback) {
        if(isDefined(value) && !isNumberRepresentation(value)) {
            logConsoleError(`[trackEvent]: Value parameter must be a number or number representation. Received: ${JSON.stringify(value)}`);
        }

        trackCallback(function () {
            logEvent(category, action, name, value, customData, callback);
        });
    };

    /**
     * Log special pageview: Internal search
     *
     * @param {string} keyword
     * @param {string|Array<string> | null | false} [category]
     * @param {number | string} [resultsCount]
     * @param {Record<string, string>} [customData]
     */
    this.trackSiteSearch = function (keyword, category, resultsCount, customData) {
        trackCallback(function () {
            logSiteSearch(keyword, category, resultsCount, customData);
        });
    };

    /**
     * Used to record that the current page view is an item (product) page view, or a Ecommerce Category page view.
     * This must be called before trackPageView() on the product/category page.
     * It will set 3 custom variables of scope "page" with the SKU, Name and Category for this page view.
     * Note: Custom Variables of scope "page" slots 3, 4 and 5 will be used.
     *
     * On a category page, you can set the parameter category, and set the other parameters to empty string or false
     *
     * Tracking Product/Category page views will allow Piwik to report on Product & Categories
     * conversion rates (Conversion rate = Ecommerce orders containing this product or category / Visits to the product or category)
     *
     * @param {string} sku Item's SKU code being viewed
     * @param {string | undefined} name Item's Name being viewed
     * @param {string | string[]} category Category page being viewed. On an Item's page, this is the item's category
     * @param {number | string} [price] Item's display price, not use in standard Piwik reports, but output in API product reports.
     */
    this.setEcommerceView = function (sku, name, category, price) {
        if (!isDefined(category) || !category.length) {
            category = "";
        } else if (category instanceof Array) {
            category = JSON.stringify(category);
        }

        customVariablesPage[5] = ['_pkc', category];

        if (isDefined(price) && String(price).length) {
            customVariablesPage[2] = ['_pkp', /** @type {string} */ (/** @type {unknown} */(price))];
        }

        // On a category page, do not track Product name not defined
        if ((!isDefined(sku) || !sku.length)
                && (!isDefined(name) || !name.length)) {
            return;
        }

        if (isDefined(sku) && sku.length) {
            customVariablesPage[3] = ['_pks', sku];
        }

        if (!isDefined(name) || !name.length) {
            name = "";
        }

        customVariablesPage[4] = ['_pkn', name];
    };

    /**
     * Returns the object containing ecommerce items that will be sent when a cart update or order is tracked.
     * The returned value is read-only, modifications will not change what will be tracked. Use
     * addEcommerceItem/removeEcommerceItem/clearEcommerceCart to modify what items will be tracked.
     *
     * Note: the cart will be cleared after an order.
     *
     * @returns object
     */
    this.getEcommerceItems = function () {
        return JSON.parse(JSON.stringify(ecommerceItems));
    };

    /**
     * Adds an item (product) that is in the current Cart or in the Ecommerce order.
     * This function is called for every item (product) in the Cart or the Order.
     * The only required parameter is sku.
     * The items are deleted from this JavaScript object when the Ecommerce order is tracked via the method trackEcommerceOrder.
     *
     * If there is already a saved item for the given sku, it will be updated with the
     * new information.
     *
     * @param {string} sku (required) Item's SKU Code. This is the unique identifier for the product.
     * @param {string} [name] (optional) Item's name
     * @param {string | string[]} [category] (optional) Item's category, or array of up to 5 categories
     * @param {number} [price] (optional) Item's price. If not specified, will default to 0
     * @param {number} [quantity] (optional) Item's quantity. If not specified, will default to 1
     */
    this.addEcommerceItem = function (sku, name, category, price, quantity) {
        if (sku.length) {
            ecommerceItems[sku] = [ sku, name, category, price, quantity ];
        }
    };

    /**
     * Removes a single ecommerce item by SKU from the current cart.
     *
     * @param {string} sku (required) Item's SKU Code. This is the unique identifier for the product.
     */
    this.removeEcommerceItem = function (sku) {
        if (sku.length) {
            delete ecommerceItems[sku];
        }
    };

    /**
     * Clears the current cart, removing all saved ecommerce items. Call this method to manually clear
     * the cart before sending an ecommerce order.
     */
    this.clearEcommerceCart = function () {
        ecommerceItems = {};
    };

    /**
     * Tracks an Ecommerce order.
     * If the Ecommerce order contains items (products), you must call first the addEcommerceItem() for each item in the order.
     * All revenues (grandTotal, subTotal, tax, shipping, discount) will be individually summed and reported in Piwik reports.
     * Parameters orderId and grandTotal are required. For others, you can set to false if you don't need to specify them.
     * After calling this method, items added to the cart will be removed from this JavaScript object.
     *
     * @param {string | number} orderId (required) Unique Order ID.
     *                   This will be used to count this order only once in the event the order page is reloaded several times.
     *                   orderId must be unique for each transaction, even on different days, or the transaction will not be recorded by Piwik.
     * @param {number} grandTotal (required) Grand Total revenue of the transaction (including tax, shipping, etc.)
     * @param {number} [subTotal] (optional) Sub total amount, typically the sum of items prices for all items in this order (before Tax and Shipping costs are applied)
     * @param {number} [tax] (optional) Tax amount for this order
     * @param {number} [shipping] (optional) Shipping amount for this order
     * @param {number} [discount] (optional) Discounted amount in this order
     */
    this.trackEcommerceOrder = function (orderId, grandTotal, subTotal, tax, shipping, discount) {
        logEcommerceOrder(orderId, grandTotal, subTotal, tax, shipping, discount);
    };

    /**
     * Tracks a Cart Update (add item, remove item, update item).
     * On every Cart update, you must call addEcommerceItem() for each item (product) in the cart, including the items that haven't been updated since the last cart update.
     * Then you can call this function with the Cart grandTotal (typically the sum of all items' prices)
     * Calling this method does not remove from this JavaScript object the items that were added to the cart via addEcommerceItem
     *
     * @param {number} grandTotal (required) Items (products) amount in the Cart
     */
    this.trackEcommerceCartUpdate = function (grandTotal) {
        logEcommerceCartUpdate(grandTotal);
    };

    /**
     * @param {Product[]} products 
     * @param {EcommerceOptions} [options] 
     */
    this.ecommerceProductDetailView = function(products, options) {
        if (!validateEcommerceOptionsParameter(options, 'ecommerceProductDetailView')) {
            options = undefined
        }

        if(ecommerceValidateProducts(products)) {
            ecommerceTrackEvent('product-detail-view', products, undefined, options);
        }
    };

    /**
     * @param {Product[]} products
     * @param {EcommerceOptions} [options]
     */
    this.ecommerceAddToCart = function(products, options) {
        if (!validateEcommerceOptionsParameter(options, 'ecommerceAddToCart')) {
            options = undefined;
        }

        if(ecommerceValidateProducts(products)) {
            ecommerceTrackEvent('add-to-cart', products, undefined, options);
        }
    };

    /**
     * @param {Product[]} products
     * @param {EcommerceOptions} [options]
     */
    this.ecommerceRemoveFromCart = function(products, options) {
        if (!validateEcommerceOptionsParameter(options, 'ecommerceRemoveFromCart')) {
            options = undefined;
        }

        if(ecommerceValidateProducts(products)) {
            ecommerceTrackEvent('remove-from-cart', products, undefined, options);
        }
    };

    this.ecommerceCartUpdate = ecommerceCartUpdate;

    this.ecommerceOrder = ecommerceOrder;

    /**
     * Send error event to tracker
     *
     * @param {Error} error
     */
    this.trackError = function (error) {
        var stackFrames, lastFrame;

        if(error) {
            try {
                stackFrames = ErrorStackParser.parse(error);
                lastFrame = stackFrames[0];
            } catch (e) {
                // pass
            }

            logErrorEvent(
                _get(error, 'message'),
                _get(lastFrame, 'fileName'),
                _get(lastFrame, 'lineNumber'),
                _get(lastFrame, 'columnNumber')
            );
        }
    };

    /**
     * @fake-private
     * Sends a tracking request with custom request parameters.
     * Piwik will prepend the hostname and path to Piwik, as well as all other needed tracking request
     * parameters prior to sending the request. Useful eg if you track custom dimensions via a plugin.
     *
     * @param {string} request eg. "param=value&param2=value2"
     * @param {Record<string, string>} [customData]
     * @param {() => void} [callback]
     * @param {string} [pluginMethod]
     */
    this.trackRequest = function (request, customData, callback, pluginMethod) {
        trackCallback(function () {
            var fullRequest = getRequest(request, customData, pluginMethod);
            sendRequest(fullRequest, configTrackerPause, callback);
        });
    };

    /**
     * Disables user anonymization and sends deanonymization request.
     * To be used after user gave consent for tracking
     */
    this.deanonymizeUser = function () {
        this.setUserIsAnonymous(false);

        var requestPing = getRequest('ping=4', null, 'ping');
        sendRequest(requestPing, configTrackerPause);
    };

    /**
     * Returns tracking source name and version stored in the tracker instance.
     * If not overwritten, that will be ['jstc', '<version from package.json>'].
     * @returns A tuple with tracking source name and version
     */
    this.getTrackingSource = function () {
        return [trackingSourceName, trackingSourceVersion];
    };

    /**
     * Overwrites tracking source name and version stored in the tracker instance.
     * @param {string | number} name Tracking source name, e.g. 'jstc'
     * @param {string | number} [version] Tracking source version, e.g. '2.3.1'. `undefined` will keep the current version.
     */
    this.setTrackingSource = function (name, version) {
        trackingSourceName = String(name);

        if (version !== undefined) {
            trackingSourceVersion = String(version);
        }
    };

    /**
     * Metadata set with this function takes precedance over the one set with `setTrackingSource`
     * 
     * @param {string} provider Overrides &ts_n parameter
     * @param {string} providerVersion Overrides &ts_v parameter
     */
    this.setTrackingSourceProvider = function (provider, providerVersion) {
      if (typeof provider !== 'string') {
        logConsoleError("[setTrackingSourceProvider]: 'provider' has to be of type string");
        return;
      }
      if (typeof providerVersion !== 'string') {
        logConsoleError("[setTrackingSourceProvider]: 'providerVersion' has to be of type string");
        return;
      }

      trackingSourceProvider = provider;
      trackingSourceProviderVersion = providerVersion
    };

    /**
     * Returns tracking source provider name and version stored in the tracker instance.
     * If not set by `setTrackingSourceProvider`, it will return `undefined`.
     * @returns {[string, string] | undefined}
     */
    this.getTrackingSourceProvider = function () {
        if(trackingSourceProvider){
            return [trackingSourceProvider, trackingSourceProviderVersion]
        }

        return undefined
    }

    /**
     * @param {boolean} value
     */
    this.setIpTracking = function(value) {
        ipTracking = value;
    };

    /**
     * @param {boolean} value
     */
    this.setDeviceDataAnonymization = function(value) {
        if ('boolean' !== typeof value) {
            logConsoleError('setDeviceDataAnonymization: The value for device data anonymization must be a boolean');
            return;
        }
        deviceDataAnonymize = value;
    }

    /**
     * @param {boolean} value
     */
    this.setEnableClientHints = function (value) {
      if (typeof value !== 'boolean') {
        logConsoleError(
          '[setEnableClientHints]: The value for enabling client hints must be a boolean'
        );
        return;
      }
      clientHintsEnabled = value;
    };

    Piwik.trigger('TrackerSetup', [this]);
}

function TrackerProxy() {
    return {
        push: apply
    };
}

/**
 * Applies the given methods in the given order if they are present in paq.
 *
 * @param {([string, ...unknown[]] | [Function])[]} queue
 * @param {Array<string>} methodsToApply an array containing method names in the order that they should be applied
 *                 eg ['setSiteId', 'setTrackerUrl']
 */
function applyMethodsInOrder(queue, methodsToApply)
{
    /** @type {Record<string, number>} */
    var appliedMethods = {};
    var index, iterator;

    for (index = 0; index < methodsToApply.length; index++) {
        var methodNameToApply = methodsToApply[index];
        appliedMethods[methodNameToApply] = 1;

        for (iterator = 0; iterator < queue.length; iterator++) {
            if (queue[iterator] && queue[iterator][0]) {
                var methodName = queue[iterator][0];

                if (methodNameToApply === methodName) {
                    apply(queue[iterator]);
                    delete queue[iterator];

                    if (appliedMethods[methodName] > 1
                        && methodName !== "addTracker"
                        && methodName !== "enableLinkTracking"
                        && methodName !== "setSecureCookie"
                        && methodName !== "enableCrossDomainLinking"
                    )
                    {
                        logConsoleError('The method ' + methodName + ' is registered more than once in "' + config.queueName + '" variable. Only the last call has an effect. Please have a look at the multiple Piwik trackers documentation');
                    }

                    appliedMethods[methodName]++;
                }
            }
        }
    }

    // Cookie is set after all priority methods are processed since they may influence cookie settings
    // e.g. disableCookies, setSecureCookie, setCookiePath, setCookieDomain
    apply(['setVisitorIdCookie']);
}

/************************************************************
 * Constructor
 ************************************************************/

/** @type {(keyof Tracker)[]} */
const applyFirst = ['addTracker', 'setTrackingSourceProvider', 'disableCookies', 'enableCookies', 'setSessionIdStrictPrivacyMode', 'setTrackerUrl', 'setAPIUrl', 'enableCrossDomainLinking', 'setCrossDomainLinkingTimeout', 'customCrossDomainLinkDecorator', 'customCrossDomainLinkVisitorIdGetter', 'setSecureCookie', 'setCookiePath', 'setCookieDomain', 'setDomains', 'setUserId', 'setSiteInspectorSetup', 'setSiteId', 'enableLinkTracking', 'setCustomVariableMaximumLength', 'setDeviceDataAnonymization', 'setEnableClientHints', 'setUserIsAnonymous'];
/** @type {(keyof Tracker)[]} */
const applyLast = ['trackError'];

/**
 * @param {string} piwikUrl
 * @param {string} siteId
 * @returns {Tracker}
 */
function createFirstTracker(piwikUrl, siteId)
{
    var tracker = new Tracker(piwikUrl, siteId);
    asyncTrackers.push(tracker);

    // @ts-expect-error command queue array
    const initialQueue = window[config.queueName].splice(0, window[config.queueName].length)

    const { toApplyFirst, toApplySecond, toApplyLast } = organizeQueue(initialQueue, applyFirst, applyLast);

    applyMethodsInOrder(toApplyFirst, applyFirst);

    for (let i = 0; i < toApplySecond.length; i++) {
        apply(toApplySecond[i]);
    }

    // process any items that were dynamically added to the queue during processing.
    // @see /tests/initialQueueProcessing/dynamicallyAddedEntries.spec.js
    // @ts-expect-error command queue array
    const everGrowingQueue = window[config.queueName]
    for (let i = 0; i < everGrowingQueue.length; i++) {
        apply(everGrowingQueue[i]);
    }

    for (let i = 0; i < toApplyLast.length; i++) {
        apply(toApplyLast[i]);
    }

    // replace initialization array with proxy object
    var trackerInitErrorMessage = (
        'Matomo javascript tracker or Piwik PRO javascript tracker was already initiated on this page. ' +
        'Please check the tracker setup. Matomo conflict can be resolved using these instructions: ' +
        'https://help.piwik.pro/support/questions/can-i-use-piwik-pro-and-matomo-at-the-same-time/.'
    );
    // @ts-expect-error command queue array
    if(typeof window[config.queueName].length === 'number') {
        var trackerProxy = TrackerProxy();
        var startTime = Date.now();

        // @ts-expect-error command queue array
        window[config.queueName] = trackerProxy;
        var interval = setInterval(
            function() {
                if((Date.now() - startTime) > 60_000) {
                    clearInterval(interval);
                    return;
                }
                // @ts-expect-error command queue array
                if(window[config.queueName] !== trackerProxy) {
                    logConsoleError(trackerInitErrorMessage);
                    clearInterval(interval);
                    return;
                }
            },
            1000
        );
    } else {
        logConsoleError(trackerInitErrorMessage);
    }

    return tracker;
}

/************************************************************
 * Proxy object
 * - this allows the caller to continue push()'ing to _paq
 *   after the Tracker has been initialized and loaded
 ************************************************************/

// initialize the Piwik singleton
addEventListenerWrapper(
  windowAlias,
  'beforeunload',
  () => beforeUnloadHandler({ syncTrackers, asyncTrackers }, expireDateTime),
  false
);

/************************************************************
 * Public data and methods
 ************************************************************/

Piwik = {
    initialized: false,

    JSON: JSON,

    /**
     * DOM Document related methods
     */
    DOM: {
        /**
         * Adds an event listener to the given element.
         * @param {HTMLElement} element
         * @param {string} eventType
         * @param {() => void} eventHandler
         * @param {boolean} [useCapture] Optional
         */
        addEventListener: function (element, eventType, eventHandler, useCapture) {
            var captureType = typeof useCapture;
            if (captureType === 'undefined') {
                useCapture = false;
            }

            addEventListenerWrapper(element, eventType, eventHandler, useCapture);
        },
        /**
         * Specify a function to execute when the DOM is fully loaded.
         *
         * If the DOM is already loaded, the function will be executed immediately.
         *
         * @param function callback
         */
        onLoad: trackCallbackOnLoad,

        /**
         * Specify a function to execute when the DOM is ready.
         *
         * If the DOM is already ready, the function will be executed immediately.
         *
         * @param function callback
         */
        onReady: trackCallbackOnReady,

        /**
         * Detect whether a node is visible right now.
         */
        isNodeVisible: isVisible,

        /**
         * Detect whether a node has been visible at some point
         */
        isOrWasNodeVisible: content.isNodeVisible
    },

    /**
     * Listen to an event and invoke the handler when a the event is triggered.
     *
     * @param {string} event
     * @param {() => void} handler
     */
    on: function (event, handler) {
        if (!eventHandlers[event]) {
            eventHandlers[event] = [];
        }

        eventHandlers[event].push(handler);
    },

    /**
     * Remove a handler to no longer listen to the event. Must pass the same handler that was used when
     * attaching the event via ".on".
     * @param {string} event
     * @param {() => void} handler
     */
    off: function (event, handler) {
        if (!eventHandlers[event]) {
            return;
        }

        var i = 0;
        for (i; i < eventHandlers[event].length; i++) {
            if (eventHandlers[event][i] === handler) {
                eventHandlers[event].splice(i, 1);
            }
        }
    },

    /**
     * Triggers the given event and passes the parameters to all handlers.
     *
     * @param {string} event
     * @param {unknown[]} extraParameters
     * @param {Window} [context] If given the handler will be executed in this context
     */
    trigger: function (event, extraParameters, context) {
        if (!eventHandlers[event]) {
            return;
        }

        var i = 0;
        for (i; i < eventHandlers[event].length; i++) {
            eventHandlers[event][i].apply(context || windowAlias, extraParameters);
        }
    },

    /**
     * Get Tracker (factory method, creates new tracker)
     *
     * @param {string} piwikUrl
     * @param {string} siteId
     * @return {Tracker}
     */
    getTracker: function (piwikUrl, siteId) {
        if (!isDefined(siteId)) {
            siteId = this.getAsyncTracker().getSiteId();
        }
        if (!isDefined(piwikUrl)) {
            piwikUrl = this.getAsyncTracker().getTrackerUrl();
        }

        var tracker = new Tracker(piwikUrl, siteId);
        syncTrackers.push(tracker);
        return tracker;
    },

    /**
     * Get all created async trackers
     *
     * @return Tracker[]
     */
    getAsyncTrackers: function () {
        return asyncTrackers;
    },

    /**
     * Adds a new tracker. All sent requests will be also sent to the given siteId and piwikUrl.
     * If piwikUrl is not set, current url will be used.
     *
     * @param {string} piwikUrl  If null, will reuse the same tracker URL of the current tracker instance
     * @param {string} siteId
     * @return {Tracker}
     */
    addTracker: function (piwikUrl, siteId) {
        if (!asyncTrackers.length) {
            return createFirstTracker(piwikUrl, siteId);
        } else {
            return asyncTrackers[0].addTracker(piwikUrl, siteId);
        }
    },

    /**
     * Get internal asynchronous tracker object.
     *
     * If no parameters are given, it returns the internal asynchronous tracker object. If a piwikUrl and idSite
     * is given, it will try to find an optional
     *
     * @param {string} piwikUrl
     * @param {string} siteId
     * @returns {Tracker | undefined}
     */
    getAsyncTracker: function (piwikUrl, siteId) {

        var firstTracker;
        if (asyncTrackers && asyncTrackers.length && asyncTrackers[0]) {
            firstTracker = asyncTrackers[0];
        } else {
            return createFirstTracker(piwikUrl, siteId);
        }

        if (!siteId && !piwikUrl) {
            // for BC and by default we just return the initially created tracker
            return firstTracker;
        }

        // we look for another tracker created via `addTracker` method
        if ((!isDefined(siteId) || null === siteId) && firstTracker) {
            siteId = firstTracker.getSiteId();
        }

        if ((!isDefined(piwikUrl) || null === piwikUrl) && firstTracker) {
            piwikUrl = firstTracker.getTrackerUrl();
        }

        var tracker, i = 0;
        for (i; i < asyncTrackers.length; i++) {
            tracker = asyncTrackers[i];
            if (tracker
                && String(tracker.getSiteId()) === String(siteId)
                && tracker.getTrackerUrl() === piwikUrl) {

                return tracker;
            }
        }
    },
};

// Expose Piwik as an AMD module
// @ts-expect-error who knows who uses this
if (typeof define === 'function' && define.amd) {
    // @ts-expect-error who knows who uses this
    define('piwik', [], function () { return Piwik; });
}

export default Piwik;
